import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Sheet, SheetContent, SheetFooter, SheetHeader } from '../UI/sheet';
import { BuilderSheetModeType } from './POs';
import { Button } from '../ui/button';
import {
  Loader2,
  LucideCalendar,
  LucideCheck,
  LucideChevronDown,
  LucideEye,
  LucideFileText,
  LucidePencil,
  LucidePlus,
  LucideSend,
  LucideTriangleAlert,
  LucideX,
  Trash2,
  X,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../../lib/utils';
import moment from 'moment';
import { Calendar } from '../UI/calendar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../UI/dropdown-menu';
import {
  componentQuantity,
  determineItemCost,
  determineNonMyShopItemDescription,
  determineStatusColor,
} from './helpers';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  addRemainingZeros,
  deepEqual,
  extractParent,
  // resolveItemBillingTypeClientBased,
} from '../../helper/helpers';
import { Link } from 'react-router-dom';
// import SupplierModal from '../../containers/supplier/supplier_modal';
// import { SuppliersModule } from '../../redux';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
} from '../UI/alert-dialog';
import { Checkbox } from '../ui/checkbox';
import { decimalMinusNumbersRegex, decimalNumbersRegex } from '../../helper/regex';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { resizeTextarea } from '../../helper/elements';
import {
  editPOs,
  createPO,
  downloadPOPdf,
  getPOComponents,
  getScopedAccountId,
  sendPO as sendPOApi,
} from '../../api/pos';
import { Loader } from '../../ui_components/loader';
import { resolveImageSrc } from '../../utils/image-src';
import { valuteNumberFormatter } from '../../helper/numberFormatter';
import { toast } from 'sonner';
import SendPODialog, { POEmailDraft } from './SendPODialog';

type POsBuilderProps = {
  open: boolean;
  mode: BuilderSheetModeType;
  row: any;
  footerActions: {
    save(): void;
    saveAndSend(): void;
    edit(): void;
    cancel(mode: BuilderSheetModeType): void;
    sendPO(): void;
  };
  suppliers: any[];
  client: any;
  account: any;
  user: any;
  poNextNumber: string;
  existingPOs?: any[];
  handleLinkedPOValue(val: string): void;
  handleTriggerProjectRefetch(): void;
};

type AlertModalType = {
  active: boolean;
  title: string;
  description: string;
  cancel: {
    title: string;
    action(): void;
  };
  confirm: {
    title: string;
    action(): void;
  };
};

let initialAlertModalState: AlertModalType = {
  active: false,
  title: '',
  description: '',
  cancel: {
    title: 'Cancel',
    action: () => {},
  },
  confirm: {
    title: 'Exit',
    action: () => {},
  },
};

type ShippingInformationsType = {
  howToShip: string;
  shipTo: string;
  notes: string;
};

let initialShippingInformations: ShippingInformationsType = {
  howToShip: '',
  shipTo: '',
  notes: '',
};

type AccountInformationsType = {
  contact: string;
};

let initialAccountInformations: AccountInformationsType = {
  contact: '',
};

// type SupplierModalType = {
//   active: boolean;
// };

// let initialSupplierModal: SupplierModalType = {
//   active: false,
// };

type InitialPriceAdjustmentType = {
  id: any;
  name: string;
  type: string;
  value: string;
};

let initialPriceAdjustment: InitialPriceAdjustmentType = {
  id: crypto.randomUUID(),
  name: '',
  type: 'amount',
  value: '',
};

const POsBuilder = (props: POsBuilderProps) => {
  const {
    open,
    mode,
    row,
    footerActions,
    suppliers,
    client,
    account,
    user,
    poNextNumber,
    existingPOs = [],
    handleLinkedPOValue,
    handleTriggerProjectRefetch,
  } = props;
  const { save, edit, cancel } = footerActions;

  // --- REFS ---
  // --- END OF REFS ---

  // --- MODALS AND ALERTS ---
  const [alertModal, setAlertModal] = useState<AlertModalType>(initialAlertModalState);
  // const [supplierModal, setSupplierModal] = useState<SupplierModalType>(initialSupplierModal);
  // --- END OF MODALS AND ALERTS ---
  // --- FETCH STATE ---
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [generatingPdf, setGeneratingPdf] = useState<boolean>(false);
  const [supplierComponents, setSupplierComponents] = useState<any[]>([]);
  // --- END OF FETCH STATE ---
  const [sendDialogOpen, setSendDialogOpen] = useState<boolean>(false);
  const [sendIdempotencyKey, setSendIdempotencyKey] = useState<string>('');
  // --- FORM ---
  const [paidAmount, setPaidAmount] = useState<string>('');
  const [poNumber, setPoNumber] = useState<string>('');
  const [supplier, setSupplier] = useState<any>(null);
  const [status, setStatus] = useState<{ value: string }>({
    value: 'Created',
  });
  const [statusDate, setStatusDate] = useState<Date | undefined>(undefined);
  const [etaDate, setEtaDate] = useState<Date | undefined>(undefined);
  const [sentDate, setSentDate] = useState<Date | undefined>(undefined);
  const [createdDate, setCreatedDate] = useState<Date | undefined>(undefined);
  const [docRef, setDocRef] = useState<string>('');
  const [shippingInformations, setShippingInformations] = useState<ShippingInformationsType>(
    initialShippingInformations
  );
  const [accountInformations, setAccountInformations] = useState<AccountInformationsType>(
    initialAccountInformations
  );
  const [priceAdjustments, setPriceAdjustments] = useState<InitialPriceAdjustmentType[]>([]);
  const [selectedComponents, setSelectedComponents] = useState<any[]>([]);
  const [sidemark, setSidemark] = useState<string>('');
  // --- END OF FORM ---
  // --- FORM COPY ---
  const [supplierCopy, setSupplierCopy] = useState<any>(null);
  const [statusCopy, setStatusCopy] = useState<{ value: string }>({
    value: 'Created',
  });
  const [statusDateCopy, setStatusDateCopy] = useState<Date | undefined>(undefined);
  const [etaDateCopy, setEtaDateCopy] = useState<Date | undefined>(undefined);
  const [sentDateCopy, setSentDateCopy] = useState<Date | undefined>(undefined);
  const [createdDateCopy, setCreatedDateCopy] = useState<Date | undefined>(undefined);
  const [docRefCopy, setDocRefCopy] = useState<string>('');
  const [shippingInformationsCopy, setShippingInformationsCopy] =
    useState<ShippingInformationsType>(initialShippingInformations);
  const [accountInformationsCopy, setAccountInformationsCopy] = useState<AccountInformationsType>(
    initialAccountInformations
  );
  const [priceAdjustmentsCopy, setPriceAdjustmentsCopy] = useState<InitialPriceAdjustmentType[]>(
    []
  );
  const [selectedComponentsCopy, setSelectedComponentsCopy] = useState<any[]>([]);
  const [sidemarkCopy, setSidemarkCopy] = useState<string>('');
  // --- END OF FORM COPY ---
  // --- OTHER STATES ---
  const [openedRooms, setOpenedRooms] = useState<any[]>([]);
  // --- END OF OTHER STATES ---
  // --- FUNCTIONS ---
  const handleChangeSidemark = (e: ChangeEvent<HTMLInputElement>) => {
    let { value } = e.target;
    setSidemark(value);
  };

  const handleOpenRooms = (roomKey: any) => {
    if (!roomKey) return;

    setOpenedRooms((prev) => {
      return prev?.includes(roomKey) ? prev?.filter((f: any) => f !== roomKey) : [...prev, roomKey];
    });
  };

  const handlePaidAmount = (e: ChangeEvent<HTMLInputElement>) => {
    let { value } = e.target;
    if (value && !decimalNumbersRegex.test(value)) return;
    setPaidAmount(value);
  };

  const handlePaidAmountBlur = (e: ChangeEvent<HTMLInputElement>) => {
    let { value } = e.target;

    if (value) {
      value += addRemainingZeros(value);
    }

    setPaidAmount(value);
  };

  const generatePoNumber = () => {
    let now = new Date();
    let month = String(now.getMonth() + 1).padStart(2, '0');
    let day = String(now.getDate()).padStart(2, '0');
    let year = now.getFullYear();

    let nextNumber = poNextNumber || '001';

    return `PO-${year}${month}${day}-${nextNumber}`;
  };

  const normalizeDateValue = (value?: Date | string | null) => {
    if (!value) return undefined;
    const parsed = value instanceof Date ? value : new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  };

  const normalizeInvoiceStatus = (value?: string | null) => {
    const normalized = String(value || '')
      .trim()
      .toLowerCase();
    if (!normalized || normalized === 'select' || normalized === 'not created') {
      return 'Not Created';
    }
    if (normalized === 'paid' || normalized === 'yes') return 'Paid';
    if (normalized === 'partial') return 'Partial';
    if (normalized === 'not paid' || normalized === 'no') return 'Not Paid';
    return value || 'Not Created';
  };

  // const getInvoiceBadgeClass = (value?: string | null) => {
  //   const normalized = normalizeInvoiceStatus(value).toLowerCase();
  //   if (normalized === 'paid') return 'bg-emerald-100 text-emerald-700';
  //   if (normalized === 'partial') return 'bg-amber-100 text-amber-700';
  //   if (normalized === 'not paid') return 'bg-rose-100 text-rose-700';
  //   return 'bg-slate-100 text-slate-600';
  // };

  const getPoBadgeClass = (hasPO: boolean) => {
    return hasPO ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600';
  };

  const getPurchaseOrderBodyGroups = (purchaseOrderBody: any) => {
    if (!purchaseOrderBody) return [];
    if (purchaseOrderBody?.components && !Array.isArray(purchaseOrderBody)) {
      return [purchaseOrderBody];
    }
    if (Array.isArray(purchaseOrderBody)) return purchaseOrderBody;
    if (typeof purchaseOrderBody === 'string') {
      try {
        const parsed = JSON.parse(purchaseOrderBody);
        return Array.isArray(parsed) ? parsed : [];
      } catch (_error) {
        return [];
      }
    }
    return [];
  };

  const normalizePOKeyPart = (value: any) => {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  };

  const compactPOKey = (...parts: any[]) => {
    const normalizedParts = parts.map(normalizePOKeyPart);
    if (normalizedParts.some((part) => !part)) return '';
    return normalizedParts.join('|');
  };

  const getRoomGroupKey = (room: any) => {
    return compactPOKey('room', room?.roomId, room?.roomName);
  };

  const getComponentPayload = (componentRow: any) => {
    return componentRow?.component || componentRow?.components || componentRow || {};
  };

  const getComponentReferenceValues = (componentRow: any) => {
    const component = getComponentPayload(componentRow);
    const orderId =
      componentRow?.order?.id ?? component?.orderId ?? component?.draperyOrderId ?? '';
    const roomId = componentRow?.roomId ?? component?.roomId ?? '';
    const componentId = component?.id ?? component?.draperyOrderSummaryComponent?.id ?? '';

    return {
      source: componentRow?.source || component?.source || '',
      orderId,
      roomId,
      componentId,
      componentIndex: componentRow?.componentIndex ?? component?.componentIndex ?? '',
      componentType: componentRow?.componentType || component?.type || '',
    };
  };

  const getComponentPOKeys = (componentRow: any) => {
    const values = getComponentReferenceValues(componentRow);
    const keys = [
      compactPOKey(
        'exact',
        values.source,
        values.orderId,
        values.roomId,
        values.componentId,
        values.componentIndex,
        values.componentType
      ),
      compactPOKey('source-order-component', values.source, values.orderId, values.componentId),
      compactPOKey('source-room-component', values.source, values.roomId, values.componentId),
      compactPOKey('source-component', values.source, values.componentId),
    ].filter(Boolean);

    return Array.from(new Set(keys));
  };

  const getComponentPOStatus = (componentRow: any) => {
    const poByNumber = new Map<string, any>();

    getComponentPOKeys(componentRow).forEach((key) => {
      const matches = existingPOsByComponentKey.get(key) || [];
      matches.forEach((po: any) => {
        if (po?.poNumber) poByNumber.set(po.poNumber, po);
      });
    });

    const poNumbers = Array.from(poByNumber.keys());
    const firstPoNumber = poNumbers[0] || '';
    const formattedPoNumber = firstPoNumber.startsWith('#') ? firstPoNumber : `#${firstPoNumber}`;
    const extraCount = Math.max(poNumbers.length - 1, 0);

    return {
      hasPO: poNumbers.length > 0,
      label: poNumbers.length > 0 ? formattedPoNumber : 'Not Created',
      extraCount,
    };
  };

  const handleLoading = (val: boolean) => {
    setLoading(val);
  };

  const handleCloseAlertModal = () => {
    setAlertModal((prev) => ({
      ...initialAlertModalState,
      title: prev?.title,
      description: prev?.description,
      cancel: {
        ...initialAlertModalState?.cancel,
        title: prev?.cancel?.title,
      },
      confirm: {
        ...initialAlertModalState?.confirm,
        title: prev?.confirm?.title,
      },
    }));
  };

  const handleSelectComponents = (
    checked: string | boolean,
    type: 'all' | 'room' | 'single',
    room?: any,
    item?: any
  ) => {
    switch (type) {
      case 'all':
        let all_arr: any[] = [];

        visibleSupplierComponents.forEach((i: any) => {
          all_arr.push({
            components: checked ? i?.components : [],
            roomId: i?.roomId,
            roomName: i?.roomName,
          });
        });

        setSelectedComponents(all_arr);
        return;
      case 'room':
        const targetRoomKey = getRoomGroupKey(room);
        let room_data: any[] =
          visibleSupplierComponents?.find((f: any) => getRoomGroupKey(f) === targetRoomKey)
            ?.components || [];

        setSelectedComponents((prev) => {
          return prev?.map((i) => {
            if (getRoomGroupKey(i) === targetRoomKey) {
              return {
                ...i,
                components: checked ? room_data : [],
              };
            }
            return i;
          });
        });
        return;
      case 'single':
        if (!room || !item) return;

        setSelectedComponents((prev: any) => {
          return prev.map((i: any) => {
            if (getRoomGroupKey(i) === getRoomGroupKey(room)) {
              let has_component = i?.components?.some(
                (i: any) => i?.component?.id === item?.component?.id
              );

              return {
                ...i,
                components: has_component
                  ? i?.components?.filter((f: any) => f?.component?.id !== item?.component?.id)
                  : [...i?.components, item],
              };
            }
            return i;
          });
        });
        return;
      default:
        return;
    }
  };

  const handleBlurPriceAdjustments = (payload?: any) => {
    let { item, name, value } = payload || {};

    if (value) {
      if (value === '-') {
        value = '';
      } else {
        value += addRemainingZeros(value);
      }
    }

    setPriceAdjustments((prev) => {
      return item && name
        ? prev.map((i) => {
            if (i?.id === item?.id) {
              return {
                ...i,
                [name]: value || '',
              };
            }
            return i;
          })
        : prev;
    });
  };

  const handlePriceAdjustments = (type: 'add' | 'remove' | 'edit', payload?: any) => {
    let { item, name, value } = payload || {};

    switch (type) {
      case 'add':
        setPriceAdjustments((prev) => [
          ...prev,
          { ...initialPriceAdjustment, id: crypto.randomUUID() },
        ]);
        return;
      case 'remove':
        setPriceAdjustments((prev) => {
          return item ? prev.filter((i) => i?.id !== item?.id) : prev;
        });
        return;
      case 'edit':
        if (name === 'value' && value && !decimalMinusNumbersRegex.test(value)) return;

        setPriceAdjustments((prev) => {
          return item && name
            ? prev.map((i) => {
                if (i?.id === item?.id) {
                  return {
                    ...i,
                    [name]: value || '',
                  };
                }
                return i;
              })
            : prev;
        });
        return;
      default:
        return;
    }
  };

  // const handleShowSupplierModal = (value: boolean) => {
  //   setSupplierModal({ active: value });
  // };

  // const handleCloseSupplierModal = () => {
  //   setSupplierModal({ active: false });
  // };

  // const handleSupplierChanged = async (item: SuppliersModule.Supplier): Promise<void> => {};

  const handleDocRef = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setDocRef(value);
  };

  const handleChooseStatus = (value: string) => {
    setStatus({ value });
    if (value === 'Sent' && !sentDate) {
      setSentDate(new Date());
    }
  };

  const handleFetchSupplierComponents = async () => {
    try {
      handleLoading(true);

      const queryParams = {
        clientId: client?.id,
        supplierId: supplier?.id,
        accountId: account?.id,
      };

      const res = await getPOComponents(queryParams);

      setSupplierComponents(res);

      const shouldInitializeComponents =
        mode === 'create' || (mode === 'update' && !Array.isArray(row?.purchaseOrderBody));

      if (shouldInitializeComponents) {
        setSelectedComponents(
          (res || [])?.map((i: any) => {
            return {
              roomId: i?.roomId,
              roomName: i?.roomName,
              components: [],
            };
          })
        );

        setSelectedComponentsCopy(
          (res || [])?.map((i: any) => {
            return {
              roomId: i?.roomId,
              roomName: i?.roomName,
              components: [],
            };
          })
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      handleLoading(false);
    }
  };

  const handleChooseSupplier = (item: any) => {
    setSupplier((prev: any) => (prev?.id === item?.id ? null : item));
  };

  const handleSelectDate = (value: Date | undefined, key: string) => {
    switch (key) {
      case 'etaDate':
        setEtaDate(value);
        return;
      case 'statusDate':
        setStatusDate(value);
        return;
      case 'sentDate':
        setSentDate(value);
        return;
      case 'createdDate':
        setCreatedDate(value);
        return;
      default:
        return;
    }
  };

  const handleRemoveDate = (key: string) => {
    switch (key) {
      case 'statusDate':
        setStatusDate(undefined);
        return;
      case 'etaDate':
        setEtaDate(undefined);
        return;
      case 'sentDate':
        setSentDate(undefined);
        return;
      case 'createdDate':
        setCreatedDate(undefined);
        return;
      default:
        return;
    }
  };

  const handleShippingInfo = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let { name, value } = e.target;

    setShippingInformations((prev) => {
      let new_state = { ...prev, [name]: value };
      return new_state;
    });
  };

  const handleAccountInfo = (e: ChangeEvent<HTMLInputElement>) => {
    let { name, value } = e.target;

    setAccountInformations((prev) => {
      let new_state = { ...prev, [name]: value };
      return new_state;
    });
  };

  const handleResetAndCloseBuilder = () => {
    // State reset
    setPaidAmount('');
    setPoNumber('');
    setSupplier(null);
    setStatus({
      value: 'Created',
    });
    setStatusDate(undefined);
    setEtaDate(undefined);
    setSentDate(undefined);
    setCreatedDate(undefined);
    setDocRef('');
    setShippingInformations(initialShippingInformations);
    setAccountInformations(initialAccountInformations);
    setPriceAdjustments([]);
    setSelectedComponents([]);
    setSidemark('');
    // Copy state reset
    setSupplierCopy(null);
    setStatusCopy({
      value: 'Created',
    });
    setStatusDateCopy(undefined);
    setEtaDateCopy(undefined);
    setSentDateCopy(undefined);
    setCreatedDateCopy(undefined);
    setDocRefCopy('');
    setShippingInformationsCopy(initialShippingInformations);
    setAccountInformationsCopy(initialAccountInformations);
    setPriceAdjustmentsCopy([]);
    setSelectedComponentsCopy([]);
    setSidemarkCopy('');
    // Other resets
    handleCloseAlertModal();
    // setSupplierModal(initialSupplierModal);
    handleLoading(true);
    setSaving(false);
    setSending(false);
    setSendDialogOpen(false);
    setSendIdempotencyKey('');
    setSupplierComponents([]);
    setOpenedRooms([]);
    // final close sheet
    handleLinkedPOValue('');
    cancel('create');
  };

  const handleViewBuilderItem = () => {
    if (!open || mode === 'create' || !row) return;

    const hydratedSupplier =
      row?.supplier ||
      suppliers?.find((item: any) => Number(item?.id) === Number(row?.supplier?.id));
    const hydratedStatus = row?.status;
    const hydratedShippingInformations = {
      howToShip: row?.howToShip || '',
      shipTo: row?.shipTo || '',
      notes: row?.shippingNotes || '',
    };
    const hydratedAccountInformations = {
      contact: row?.contactName || '',
    };
    const hydratedAdjustments = Array.isArray(row?.adjustments) ? row.adjustments : [];
    const hydratedComponents = Array.isArray(row?.purchaseOrderBody) ? row.purchaseOrderBody : [];

    setPaidAmount(row?.paidAmount ? String(row.paidAmount) : '');
    setPoNumber(row?.poNumber || '');
    setSupplier(hydratedSupplier || null);
    setStatus({ value: hydratedStatus });
    setStatusDate(normalizeDateValue(row?.statusDate));
    setEtaDate(normalizeDateValue(row?.etaDate));
    setSentDate(normalizeDateValue(row?.sentDate));
    setCreatedDate(normalizeDateValue(row?.createdAt));
    setDocRef(row?.documentReference || '');
    setShippingInformations(hydratedShippingInformations);
    setAccountInformations(hydratedAccountInformations);
    setPriceAdjustments(hydratedAdjustments);
    setSelectedComponents(hydratedComponents);
    setSidemark(row?.sidemark || '');
    setSidemarkCopy(row?.sidemark || '');
    setSupplierCopy(hydratedSupplier || null);
    setStatusCopy({ value: hydratedStatus });
    setStatusDateCopy(normalizeDateValue(row?.statusDate));
    setEtaDateCopy(normalizeDateValue(row?.etaDate));
    setSentDateCopy(normalizeDateValue(row?.sentDate));
    setCreatedDateCopy(normalizeDateValue(row?.createdAt));
    setDocRefCopy(row?.documentReference || '');
    setShippingInformationsCopy(hydratedShippingInformations);
    setAccountInformationsCopy(hydratedAccountInformations);
    setPriceAdjustmentsCopy(hydratedAdjustments);
    setSelectedComponentsCopy(hydratedComponents);
    handleLoading(false);
  };

  const handleCancelEditMode = () => {
    let alert_title = 'Discard changes?';
    let alert_description =
      "Any edits you've made to this purchase order will be discarded and you'll return to the read-only view.";

    let alert_cancel_title = 'Keep editing';
    let alert_confirm_title = 'Discard';

    setAlertModal((prev) => {
      return {
        ...prev,
        active: true,
        title: alert_title,
        description: alert_description,
        cancel: { title: alert_cancel_title, action: () => handleCloseAlertModal() },
        confirm: {
          title: alert_confirm_title,
          action: () => {
            handleViewBuilderItem();
            handleCloseAlertModal();
            cancel('view');
          },
        },
      };
    });
  };

  const handleCloseSheet = () => {
    if (sending) return;

    const is_deep_equal = deepEqual(
      {
        supplier: supplier,
        status: status,
        statusDate: statusDate,
        etaDate: etaDate,
        sentDate: sentDate,
        createdDate: createdDate,
        docRef: docRef,
        shippingInformations: shippingInformations,
        accountInformations: accountInformations,
        priceAdjustments: priceAdjustments,
        selectedComponents: selectedComponents,
        sidemark: sidemark,
      },
      {
        supplier: supplierCopy,
        status: statusCopy,
        statusDate: statusDateCopy,
        etaDate: etaDateCopy,
        sentDate: sentDateCopy,
        createdDate: createdDateCopy,
        docRef: docRefCopy,
        shippingInformations: shippingInformationsCopy,
        accountInformations: accountInformationsCopy,
        priceAdjustments: priceAdjustmentsCopy,
        selectedComponents: selectedComponentsCopy,
        sidemark: sidemarkCopy,
      }
    );
    if (!is_deep_equal) {
      let alert_title = 'Discard unsaved changes?';
      let alert_description =
        'You have unsaved changes in this purchase order. If you exit now, your changes will be lost.';

      let alert_cancel_title = 'Cancel';
      let alert_confirm_title = 'Exit';

      setAlertModal((prev) => {
        return {
          ...prev,
          active: true,
          title: alert_title,
          description: alert_description,
          cancel: { title: alert_cancel_title, action: () => handleCloseAlertModal() },
          confirm: { title: alert_confirm_title, action: () => handleResetAndCloseBuilder() },
        };
      });
      return;
    }

    handleCloseAlertModal();
    handleResetAndCloseBuilder();
    cancel('create');
  };

  const showAccountInfoAddressStreet = () => {
    let addressStreet: string[] = [];

    let clientFacingInfo = account?.clientFacingInfo;

    if (clientFacingInfo?.address) addressStreet.push(`${clientFacingInfo?.address},`);
    if (clientFacingInfo?.unitSuite) addressStreet.push(`${clientFacingInfo?.unitSuite}`);

    if (addressStreet.length <= 0) return '';

    return addressStreet.join(' ');
  };

  const showAccountInfoAddress = () => {
    let address: string[] = [];

    let clientFacingInfo = account?.clientFacingInfo;

    if (clientFacingInfo?.city) address.push(`${clientFacingInfo?.city},`);
    if (clientFacingInfo?.state) address.push(`${clientFacingInfo?.state}`);
    if (clientFacingInfo?.zipCode) address.push(`${clientFacingInfo?.zipCode}`);

    if (address.length <= 0) return '';

    return address.join(' ');
  };

  const handleChangeComponentPoNote = (
    e: ChangeEvent<HTMLTextAreaElement>,
    room: any,
    comp: any
  ) => {
    const { name, value } = e.target;

    setSelectedComponents((prev) => {
      return prev?.map((i) => {
        if (getRoomGroupKey(i) === getRoomGroupKey(room)) {
          return {
            ...i,
            components: i?.components?.map((c: any) => {
              if (c?.component?.id === comp?.component?.id) {
                return {
                  ...c,
                  component: {
                    ...c?.component,
                    [name]: value,
                  },
                };
              }
              return c;
            }),
          };
        }
        return i;
      });
    });
  };

  const handleOpenPreview = async () => {
    await handleDownloadPdf();
  };

  const handleDownloadPdf = async () => {
    if (!supplier || generatingPdf) return;

    setGeneratingPdf(true);

    let popup: Window | null = null;
    try {
      // Keep a real window handle so we can reuse the same tab after async PDF generation.
      popup = window.open('about:blank', '_blank');
      if (popup) popup.opener = null;

      const draftPayload = buildPurchaseOrderPayload();
      const payload =
        row?.id && mode === 'view'
          ? {
              id: row.id,
              accountId: getScopedAccountId(account?.id),
            }
          : {
              accountId: draftPayload.accountId,
              purchaseOrder: {
                ...draftPayload,
                supplier: supplier || null,
              },
            };

      const res = await downloadPOPdf(payload);

      if (res?.error) {
        throw new Error(res?.message || 'Failed to generate PDF');
      }

      const downloadLink = res?.downloadLink || res?.pdfUrl;
      if (downloadLink) {
        if (popup) {
          popup.location.href = downloadLink;
        } else {
          window.open(downloadLink, '_blank', 'noopener,noreferrer');
        }
        return;
      }

      if (res?.pdfData) {
        const binary = atob(res.pdfData);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) {
          bytes[i] = binary.charCodeAt(i);
        }

        const blob = new Blob([bytes], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);

        if (popup) {
          popup.location.href = blobUrl;
        } else {
          window.open(blobUrl, '_blank', 'noopener,noreferrer');
        }
        return;
      }

      throw new Error('Invalid PDF response.');
    } catch (error: any) {
      if (popup) popup.close();
      console.error(error);
      toast.error('Could not generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const showShippingInfoSidemark = () => {
    let clientNameArr: string[] = [];
    let parentNameArr: string[] = [];

    if (client?.firstName) clientNameArr?.push(client.firstName);
    if (client?.lastName) clientNameArr?.push(client.lastName);

    let parent = extractParent(client);

    if (parent?.firstName) parentNameArr?.push(parent.firstName);
    if (parent?.lastName) parentNameArr?.push(parent.lastName);

    let finalArr: string[] = [];

    let clientName =
      clientNameArr?.length > 0 ? clientNameArr.join(', ') : client?.displayName || '';
    let parentName =
      parentNameArr?.length > 0 ? parentNameArr.join(', ') : parent?.displayName || '';

    if (clientName) finalArr.push(clientName);
    if (parentName) finalArr.push(parentName);

    if (client?.billToParent) {
      return finalArr.length > 0 ? finalArr.join(' / ') : '—';
    }
    // return clientName ? `${clientName} / ${clientName}` : '—';
    return clientName ? clientName : '—';
  };
  // --- END OF FUNCTIONS ---

  // --- USE EFFECTS ---
  useEffect(() => {
    if (!supplier) {
      setLoading(true);
    }
  }, [supplier]);

  useEffect(() => {
    if (supplier) {
      handleFetchSupplierComponents();
    } else {
      setSelectedComponents([]);
      setSelectedComponentsCopy([]);
    }
  }, [supplier]);

  useEffect(() => {
    if (open && user && mode === 'create') {
      setPoNumber((prev) => prev || generatePoNumber());
      setAccountInformations((prev) => {
        return { ...prev, contact: `${user?.firstName} ${user?.lastName}` };
      });
      setAccountInformationsCopy((prev) => {
        return { ...prev, contact: `${user?.firstName} ${user?.lastName}` };
      });
    }
  }, [open, user, mode]);

  useEffect(() => {
    if (open && account && mode === 'create') {
      let shipToPrefill: string[] = [];

      if (account?.clientFacingInfo?.companyName)
        shipToPrefill.push(account?.clientFacingInfo?.companyName);

      if (showAccountInfoAddressStreet()) shipToPrefill.push(showAccountInfoAddressStreet());
      if (showAccountInfoAddress()) shipToPrefill.push(showAccountInfoAddress());

      if (account?.clientFacingInfo?.phone) shipToPrefill.push(account?.clientFacingInfo?.phone);
      if (account?.clientFacingInfo?.email) shipToPrefill.push(account?.clientFacingInfo?.email);

      setShippingInformations((prev) => {
        return { ...prev, shipTo: shipToPrefill?.length > 0 ? shipToPrefill.join('\n') : '' };
      });
      setShippingInformationsCopy((prev) => {
        return { ...prev, shipTo: shipToPrefill?.length > 0 ? shipToPrefill.join('\n') : '' };
      });
      setSidemark(showShippingInfoSidemark());
      setSidemarkCopy(showShippingInfoSidemark());
    }
  }, [open, account, mode]);

  useEffect(() => {
    handleViewBuilderItem();
  }, [open, mode, row, suppliers]);

  useEffect(() => {
    if (!open || !supplier?.id) return;
    if (typeof window === 'undefined' || !('BroadcastChannel' in window)) return;

    const channel = new BroadcastChannel('supplier-events');

    channel.onmessage = (event) => {
      if (event.data?.type !== 'SUPPLIER_UPDATED') return;

      const updatedSupplier = event.data.supplier;
      if (!updatedSupplier?.id) return;

      if (Number(updatedSupplier.id) === Number(supplier.id)) {
        setSupplier((prev: any) => ({
          ...prev,
          ...updatedSupplier,
        }));

        setSupplierCopy((prev: any) =>
          prev?.id
            ? {
                ...prev,
                ...updatedSupplier,
              }
            : prev
        );
      }
    };

    return () => {
      channel.close();
    };
  }, [open, supplier?.id]);
  // --- END OF USE EFFECTS ---

  // --- RENDER CONSTANTS ---
  const showAddressStreet = () => {
    let addressStreet: string[] = [];

    if (supplier?.shippingAddress?.unitSuite)
      addressStreet.push(`${supplier?.shippingAddress?.unitSuite}`);
    if (supplier?.shippingAddress?.streetAddress)
      addressStreet.push(`${supplier?.shippingAddress?.streetAddress}`);

    if (addressStreet.length <= 0) return '';

    return addressStreet.join(' ');
  };

  const showAddress = () => {
    let address: string[] = [];

    if (supplier?.shippingAddress?.cityTown)
      address.push(`${supplier?.shippingAddress?.cityTown},`);
    if (supplier?.shippingAddress?.province) address.push(`${supplier?.shippingAddress?.province}`);
    if (supplier?.shippingAddress?.zipCode) address.push(`${supplier?.shippingAddress?.zipCode}`);

    if (address.length <= 0) return '';

    return address.join(' ');
  };

  const modeTitle = mode === 'create' ? 'Create' : mode === 'update' ? 'Edit' : 'View';

  const existingPOsByComponentKey = useMemo(() => {
    const poMap = new Map<string, any[]>();

    (existingPOs || []).forEach((po: any) => {
      const groups = po?.purchaseOrderBody
        ? getPurchaseOrderBodyGroups(po?.purchaseOrderBody)
        : getPurchaseOrderBodyGroups(po);

      groups.forEach((room: any) => {
        (room?.components || []).forEach((componentRow: any) => {
          const component = getComponentPayload(componentRow);
          const poInfo = {
            id: po?.id || component?.poId || component?.poNumber,
            poNumber: po?.poNumber || componentRow?.poNumber || component?.poNumber,
          };

          if (!poInfo.poNumber) return;

          getComponentPOKeys({
            ...componentRow,
            roomId: componentRow?.roomId ?? room?.roomId,
            roomName: componentRow?.roomName ?? room?.roomName,
          }).forEach((key) => {
            const current = poMap.get(key) || [];
            if (!current.some((item: any) => item?.id === poInfo.id)) {
              poMap.set(key, [...current, poInfo]);
            }
          });
        });
      });
    });

    return poMap;
  }, [existingPOs]);

  const supplierComponentKeys = useMemo(() => {
    const keys = new Set<string>();

    (supplierComponents || []).forEach((room: any) => {
      (room?.components || []).forEach((componentRow: any) => {
        getComponentPOKeys({
          ...componentRow,
          roomId: componentRow?.roomId ?? room?.roomId,
          roomName: componentRow?.roomName ?? room?.roomName,
        }).forEach((key) => {
          keys.add(key);
        });
      });
    });

    return keys;
  }, [supplierComponents]);

  const isArchivedComponentRow = (componentRow: any, room?: any) => {
    if (componentRow?.archived) return true;

    const keys = getComponentPOKeys({
      ...componentRow,
      roomId: componentRow?.roomId ?? room?.roomId,
      roomName: componentRow?.roomName ?? room?.roomName,
    });

    if (!keys.length) return false;

    return !keys.some((key) => supplierComponentKeys.has(key));
  };

  const visibleSupplierComponents = useMemo(() => {
    const rooms = (supplierComponents || []).map((room: any) => ({
      ...room,
      components: [...(room?.components || [])],
    }));
    const roomIndexes = new Map<string, number>();
    const roomComponentKeys = new Map<string, Set<string>>();

    rooms.forEach((room: any, index: number) => {
      const roomKey = getRoomGroupKey(room);
      const keys = new Set<string>();

      roomIndexes.set(roomKey, index);

      (room?.components || []).forEach((componentRow: any) => {
        getComponentPOKeys({
          ...componentRow,
          roomId: componentRow?.roomId ?? room?.roomId,
          roomName: componentRow?.roomName ?? room?.roomName,
        }).forEach((key) => {
          keys.add(key);
        });
      });

      roomComponentKeys.set(roomKey, keys);
    });

    (selectedComponents || []).forEach((room: any) => {
      const archivedComponents = (room?.components || []).filter((componentRow: any) =>
        isArchivedComponentRow(componentRow, room)
      );

      if (!archivedComponents.length) return;

      const roomKey = getRoomGroupKey(room);
      let roomIndex = roomIndexes.get(roomKey);

      if (roomIndex === undefined) {
        roomIndex = rooms.length;
        roomIndexes.set(roomKey, roomIndex);
        roomComponentKeys.set(roomKey, new Set<string>());
        rooms.push({
          roomId: room?.roomId,
          roomName: room?.roomName,
          components: [],
        });
      }

      const existingKeys = roomComponentKeys.get(roomKey) || new Set<string>();

      archivedComponents.forEach((componentRow: any) => {
        const componentKeys = getComponentPOKeys({
          ...componentRow,
          roomId: componentRow?.roomId ?? room?.roomId,
          roomName: componentRow?.roomName ?? room?.roomName,
        });

        if (componentKeys.some((key) => existingKeys.has(key))) return;

        rooms[roomIndex!] = {
          ...rooms[roomIndex!],
          components: [
            ...(rooms[roomIndex!]?.components || []),
            {
              ...componentRow,
              archived: true,
            },
          ],
        };

        componentKeys.forEach((key) => {
          existingKeys.add(key);
        });
      });
    });

    return rooms;
  }, [selectedComponents, supplierComponents, supplierComponentKeys]);

  const totalComponentsLength = useMemo(() => {
    return visibleSupplierComponents?.reduce((acc, item) => {
      let room_components = item?.components ? item?.components?.length : 0;

      acc += room_components;

      return acc;
    }, 0);
  }, [visibleSupplierComponents]);

  useEffect(() => {
    if (open && visibleSupplierComponents && visibleSupplierComponents?.length > 0) {
      setOpenedRooms(visibleSupplierComponents?.map((i: any) => getRoomGroupKey(i)));
    }
  }, [open, visibleSupplierComponents]);

  const totalSelectedComponentsLength = useMemo(() => {
    return selectedComponents?.reduce((acc, item) => {
      let room_components = item?.components ? item?.components?.length : 0;

      acc += room_components;

      return acc;
    }, 0);
  }, [selectedComponents]);

  const pricing: {
    subtotal: number;
    orderTotal: number;
    paidAmount: number;
    amountDue: number;
  } = useMemo(() => {
    let result: {
      subtotal: number;
      orderTotal: number;
      paidAmount: number;
      amountDue: number;
    } = selectedComponents.reduce(
      (acc, item) => {
        let temp_amount = 0;
        let components = item?.components || [];

        components?.forEach((i: any) => {
          // let component = i?.component || null;
          // let numCost = !resolveItemBillingTypeClientBased(
          //   client,
          //   component?.item
          // )?.method?.includes('markUp')
          //   ? Number(resolveItemBillingTypeClientBased(client, component?.item)?.value) || 0
          //   : Number(component?.cost) || 0;
          // let numQty = Number(component?.qty) || 1;
          let numCost = determineItemCost(i, client);
          let numQty = componentQuantity(i)?.quantityNumber;

          temp_amount += numCost * numQty;
        });

        acc.subtotal += Number(temp_amount.toFixed(2));

        return acc;
      },
      {
        subtotal: 0,
        orderTotal: 0,
        paidAmount: 0,
        amountDue: 0,
      }
    );

    let priceAdjustments_total = priceAdjustments?.reduce(
      (acc, item) => {
        acc[item?.type] += Number(item?.value) || 0;

        return acc;
      },
      {
        amount: 0,
        percent: 0,
      }
    );

    let total_markup = 1 + priceAdjustments_total?.percent / 100;

    let order_total = result.subtotal * total_markup + priceAdjustments_total.amount;
    let amount_due_total = order_total - (Number(paidAmount) || 0);

    result.orderTotal = Number(order_total?.toFixed(2)) || 0;
    result.amountDue = Number(amount_due_total?.toFixed(2)) || 0;

    return result;
  }, [selectedComponents, paidAmount, priceAdjustments]);
  const isCancelledPO = ['canceled', 'cancelled'].includes(
    String(status?.value || '').toLowerCase()
  );
  const sendDisabledReason = isCancelledPO ? 'Cancelled POs cannot be sent.' : '';

  const duplicateFabricWarning = useMemo(() => {
    const fabricMap = new Map<string, number>();

    selectedComponents?.forEach((room: any) => {
      room?.components?.forEach((row: any) => {
        const component = row?.component || row?.components || row || {};
        const item = component?.patternObj || component?.item || {};

        const fabricId = item?.id || component?.patternId;

        if (!fabricId) return;

        fabricMap.set(String(fabricId), (fabricMap.get(String(fabricId)) || 0) + 1);
      });
    });

    return Array.from(fabricMap.values()).some((count) => count > 1);
  }, [selectedComponents]);
  // --- END OF RENDER CONSTANTS ---

  const buildPurchaseOrderPayload = () => {
    const resolvedPoNumber = poNumber || row?.poNumber || generatePoNumber();

    return {
      ...(row?.id ? { id: row.id } : {}),
      accountId: getScopedAccountId(account?.id),
      clientId: client?.id,
      supplierId: supplier?.id,
      poNumber: resolvedPoNumber,
      poGenerated: true,
      status: status?.value || 'Created',
      statusDate:
        ['Created', 'Sent'].includes(status?.value) && !statusDate
          ? new Date()
          : statusDate || null,
      sentDate: sentDate || null,
      etaDate: etaDate || null,
      orderIds:
        (selectedComponents || [])
          ?.flatMap((i) => i?.components || [])
          ?.map((i) => i?.order?.id)
          ?.filter((f: any) => Boolean(f)) || [],
      documentReference: docRef?.trim(),
      howToShip: shippingInformations?.howToShip?.trim(),
      shippingNotes: shippingInformations?.notes?.trim(),
      sidemark: sidemark || '',
      shipTo: shippingInformations?.shipTo?.trim(),
      businessInfoJson: {
        companyName: account?.clientFacingInfo?.companyName,
        phone: account?.clientFacingInfo?.phone,
        email: account?.clientFacingInfo?.email,
        address: account?.clientFacingInfo?.address,
        unitSuite: account?.clientFacingInfo?.unitSuite,
        city: account?.clientFacingInfo?.city,
        state: account?.clientFacingInfo?.state,
        zipCode: account?.clientFacingInfo?.zipCode,
      },
      contactName: accountInformations?.contact?.trim(),
      accountNumber: supplier?.shippingAddress?.accountNumber || '',
      terms: supplier?.billingAddress?.paymentTerms || '',
      paidAmount: paidAmount,
      subtotal: pricing.subtotal?.toFixed(2),
      adjustments: priceAdjustments,
      orderTotal: pricing.orderTotal?.toFixed(2),
      amountDue: pricing?.amountDue?.toFixed(2),
      isSent: row?.isSent || false,
      lastSentAt: sentDate || null,
      sentAt: sentDate || null,
      sentBy: row?.sentBy || null,
      createdBy: row?.createdBy || user?.id,
      updatedBy: user?.id,
      purchaseOrderBody: selectedComponents,
      actionType: 'po-builder',
    };
  };

  const persistPO = async ({
    closeAfterSave = true,
    showToast = true,
    action = 'create',
  }: { closeAfterSave?: boolean; showToast?: boolean; action?: string } = {}) => {
    try {
      setSaving(true);
      let payload = buildPurchaseOrderPayload();

      let now = new Date();
      if (['save-and-send', 'send'].includes(action)) {
        setStatus({ value: 'Sent' });
        setStatusDate(now);

        payload = {
          ...payload,
          status: 'Sent',
          statusDate: now,
        };
      } else if (status?.value === 'Sent') {
      }

      const res =
        mode === 'create' || !payload.id ? await createPO(payload) : await editPOs(payload);
      const savedPO = res?.data || null;

      if (res?.error) {
        throw new Error(res?.message || 'Failed to save PO');
      }

      if (res) {
        handleTriggerProjectRefetch();
      }

      if (savedPO?.poNumber) {
        setPoNumber(savedPO.poNumber);
      }

      if (showToast) {
        toast.success(mode === 'create' ? 'PO saved' : 'PO updated');
      }

      if (closeAfterSave) {
        handleResetAndCloseBuilder();
        save();
      }

      return savedPO;
    } catch (error) {
      console.error(error);
      // toast.error(error?.response?.data?.message || error?.message || 'Failed to save PO');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSavePO = async () => {
    await persistPO({ closeAfterSave: true });
  };

  const handleOpenSendDialog = () => {
    if (!supplier) return;
    if (!poNumber) {
      setPoNumber(row?.poNumber || generatePoNumber());
    }
    setSendIdempotencyKey(
      (prev) => prev || `po-send-${row?.id || poNumber || 'draft'}-${Date.now()}`
    );
    setSendDialogOpen(true);
  };

  const handleSendDialogOpenChange = (value: boolean) => {
    setSendDialogOpen(value);
    if (!value && !sending) {
      setSendIdempotencyKey('');
    }
  };

  const handleEditRequest = () => {
    const hasBeenSent =
      row?.isSent ||
      ['sent', 'acknowledged', 'partially received', 'received', 'closed'].includes(
        String(status?.value || '').toLowerCase()
      );

    if (!hasBeenSent) {
      edit();
      return;
    }

    setAlertModal((prev) => ({
      ...prev,
      active: true,
      title: 'Edit sent PO?',
      description:
        'This PO has already been sent to the supplier. Editing it will change the saved PO record.',
      cancel: { title: 'Cancel', action: () => handleCloseAlertModal() },
      confirm: {
        title: 'Edit PO',
        action: () => {
          handleCloseAlertModal();
          edit();
        },
      },
    }));
  };

  const handleSendPO = async (draft: POEmailDraft) => {
    setSending(true);
    try {
      // const savedPO =
      //   mode === 'create' || mode === 'update'
      //     ? await persistPO({
      //         closeAfterSave: mode === 'create' ? true : false,
      //         showToast: mode === 'create' ? true : false,
      //         action: 'save-and-send',
      //       })
      //     : row || buildPurchaseOrderPayload();
      const savedPO = await persistPO({
        closeAfterSave: mode === 'create' ? true : false,
        showToast: mode === 'create' ? true : false,
        action: mode === 'view' ? 'send' : 'save-and-send',
      });

      if (!savedPO?.id) {
        throw new Error('PO must be saved before it can be sent.');
      }

      const idempotencyKey = sendIdempotencyKey || `po-send-${savedPO.id}-${Date.now()}`;
      const res = await sendPOApi({
        id: savedPO.id,
        accountId: getScopedAccountId(account?.id),
        clientId: client?.id,
        to: draft.to,
        cc: draft.cc,
        replyTo: draft.replyTo,
        subject: draft.subject,
        body: draft.body,
        sender: {
          id: user?.id,
          firstName: user?.firstName,
          lastName: user?.lastName,
          email: user?.email,
        },
        idempotencyKey,
      });

      if (res?.error) {
        throw new Error(res?.message || 'Failed to send PO');
      }

      toast.success('PO sent');
      save();
      handleResetAndCloseBuilder();
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to send PO');
    } finally {
      setSending(false);
    }
  };

  // --- RENDER ---
  return (
    <>
      <Sheet open={open} onOpenChange={(value) => !value && handleCloseSheet()}>
        <SheetContent
          showCloseButton={false}
          side="bottom"
          className="h-full w-full max-w-none p-0 sm:max-w-none flex flex-col"
        >
          <SheetHeader
            className={cn(
              'shrink-0 z-10 flex-row bg-background border-b border-input px-4 py-3 md:px-6 md:py-4 flex justify-between gap-4',
              !supplier ? 'items-center' : 'items-start'
            )}
          >
            <div className="min-w-0">
              <div className="flex flex-col">
                <h2 className="m-0 text-[24px] font-semibold text-foreground">
                  {modeTitle} Purchase Order
                </h2>
                {supplier && (
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-xs font-normal text-muted-foreground">
                      {mode !== 'create' ? row?.poNumber : generatePoNumber()}
                    </span>
                    <div
                      className={cn(
                        'inline-flex items-center rounded-2xl pl-2.5 pr-2 py-0.5 text-xs font-medium gap-1.5 whitespace-nowrap bg-slate-100 text-slate-700',
                        determineStatusColor(status.value)
                      )}
                    >
                      <div className="inline-flex items-center gap-1.5">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              disabled={mode === 'view'}
                              data-qa={`${mode}_po_status_trigger`}
                              variant={'text'}
                              className={cn(
                                'rounded-2xl p-0 gap-1 text-xs font-medium hover:opacity-80 transition-opacity min-h-0 max-h-[20px]'
                              )}
                            >
                              <span className="wrap-text">{status?.value || '—'}</span>
                              <LucideChevronDown className="lucide lucide-chevron-down h-3 w-3 shrink-0" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="z-[99999990]" align="start">
                            <DropdownMenuItem
                              data-qa={`${mode}_po_row_status_item_created`}
                              className="relative flex select-none items-center rounded-sm px-2 py-1.5 outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer text-sm gap-0"
                              onClick={() => handleChooseStatus('Created')}
                            >
                              <span className="inline-block h-2 w-2 rounded-full mr-2 bg-slate-100"></span>
                              <span>Created</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-qa={`${mode}_po_row_status_item_sent`}
                              className="relative flex select-none items-center rounded-sm px-2 py-1.5 outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer text-sm gap-0"
                              onClick={() => handleChooseStatus('Sent')}
                            >
                              <span className="inline-block h-2 w-2 rounded-full mr-2 bg-blue-100"></span>
                              <span>Sent</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-qa={`${mode}_po_row_status_item_acknowledged`}
                              className="relative flex select-none items-center rounded-sm px-2 py-1.5 outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer text-sm gap-0"
                              onClick={() => handleChooseStatus('Acknowledged')}
                            >
                              <span className="inline-block h-2 w-2 rounded-full mr-2 bg-indigo-100"></span>
                              <span>Acknowledged</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-qa={`${mode}_po_row_status_item_partially_received`}
                              className="relative flex select-none items-center rounded-sm px-2 py-1.5 outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer text-sm gap-0"
                              onClick={() => handleChooseStatus('Partially Received')}
                            >
                              <span className="inline-block h-2 w-2 rounded-full mr-2 bg-amber-100"></span>
                              <span>Partially Received</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-qa={`${mode}_po_row_status_item_received`}
                              className="relative flex select-none items-center rounded-sm px-2 py-1.5 outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer text-sm gap-0"
                              onClick={() => handleChooseStatus('Received')}
                            >
                              <span className="inline-block h-2 w-2 rounded-full mr-2 bg-green-100"></span>
                              <span>Received</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-qa={`${mode}_po_row_status_item_closed`}
                              className="relative flex select-none items-center rounded-sm px-2 py-1.5 outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer text-sm gap-0"
                              onClick={() => handleChooseStatus('Closed')}
                            >
                              <span className="inline-block h-2 w-2 rounded-full mr-2 bg-emerald-100"></span>
                              <span>Closed</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-qa={`${mode}_po_row_status_item_canceled`}
                              className="relative flex select-none items-center rounded-sm px-2 py-1.5 outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer text-sm gap-0"
                              onClick={() => handleChooseStatus('Canceled')}
                            >
                              <span className="inline-block h-2 w-2 rounded-full mr-2 bg-red-100"></span>
                              <span>Canceled</span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <span className="opacity-40">|</span>
                      <div
                        className={cn(
                          'inline-flex items-center gap-1 group text-xs',
                          '!text-current',
                          statusDate ? '[&:hover>.builder-status-date-x]:opacity-100' : ''
                        )}
                      >
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              disabled={mode === 'view'}
                              data-qa={`${mode}_po_status_date_trigger`}
                              type="button"
                              className={cn(
                                'transition text-left',
                                !statusDate ? '!text-current' : '',
                                '!text-current hover:!text-current hover:!opacity-80'
                              )}
                            >
                              {statusDate ? moment(statusDate).format('MM/DD/YY') : 'MM/DD/YY'}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-auto p-0">
                            <Calendar
                              data-qa={`${mode}_po_status_date_calendar`}
                              mode="single"
                              selected={statusDate}
                              className="rounded-md border-0 p-[12px]"
                              onSelect={(v) => handleSelectDate(v, 'statusDate')}
                            />
                          </PopoverContent>
                        </Popover>

                        {statusDate && (
                          <Button
                            disabled={mode === 'view'}
                            data-qa={`${mode}_po_status_date_clear`}
                            onClick={() => handleRemoveDate('statusDate')}
                            className="builder-status-date-x p-0 opacity-0 h-[unset]"
                            variant={'x_btn'}
                          >
                            <LucideX className="lucide lucide-x h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <div className="flex items-center gap-2 mr-2">
                <Button
                  onClick={() => handleOpenPreview()}
                  disabled={!supplier || generatingPdf}
                  variant={'outline'}
                  className="font-medium min-h-[36px]"
                >
                  <LucideEye className="lucide lucide-eye h-4 w-4" />
                  <span>Preview</span>
                </Button>
                <Button
                  onClick={() => handleDownloadPdf()}
                  disabled={!supplier || generatingPdf}
                  variant={'outline'}
                  className="font-medium min-h-[36px]"
                >
                  {generatingPdf ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <LucideFileText className="lucide lucide-file-text h-4 w-4" />
                  )}
                  <span>{generatingPdf ? 'Generating...' : 'Export PDF'}</span>
                </Button>
              </div>

              <Button
                className="min-h-[36px]"
                variant="ghost"
                type="button"
                size="icon"
                onClick={handleCloseSheet}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </SheetHeader>

          {/* Builder body */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="flex flex-col h-full min-h-0">
              <div className="flex-1 min-h-0 overflow-auto">
                {/* CREATE VIEW */}
                {/* UPDATE VIEW */}
                {['create', 'update'].includes(mode) ? (
                  !supplier ? (
                    <>
                      <div className="border-b border-input bg-background px-4 py-4 md:px-6 md:py-5">
                        <div className="max-w-xl">
                          <div className="mb-3">
                            <h2 className="m-0 text-base font-semibold text-foreground">
                              Select a supplier
                            </h2>
                          </div>
                          <ul className="rounded-md border border-input bg-background max-h-[245px] overflow-y-auto">
                            {(suppliers || []).map((item: any) => {
                              return (
                                <li
                                  key={item?.id}
                                  className="border-b border-input last:border-none"
                                >
                                  <Button
                                    variant={'text'}
                                    onClick={() => handleChooseSupplier(item)}
                                    className="w-full flex items-center gap-1.5 px-4 py-3 text-left text-sm hover:bg-muted/40 hover:bg-muted/40 hover:outline-none transition-colors justify-start"
                                  >
                                    <span className="font-medium text-foreground">
                                      {item?.displayName || ''}
                                    </span>
                                    <span className="text-muted-foreground">
                                      ({item?.numberOfComponents || 0})
                                    </span>
                                  </Button>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                      <div className="px-4 py-10 md:px-8 md:py-16 text-center text-sm text-muted-foreground">
                        Select a supplier to preview the PO.
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="border-b border-input bg-background px-4 py-4 md:px-6 md:py-5">
                        <div className="flex flex-wrap items-end gap-4">
                          <div className="space-y-2 w-full max-w-md">
                            <Label htmlFor="supplier">Supplier</Label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  disabled={mode === 'update'}
                                  className="w-full justify-between"
                                  variant={'outline'}
                                >
                                  <span>{supplier?.displayName || '—'}</span>
                                  <LucideChevronDown className="lucide lucide-chevron-down h-4 w-4 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent align="start" className=" p-1">
                                {(suppliers || []).map((item: any) => {
                                  return (
                                    <Button
                                      key={item?.id}
                                      onClick={() => handleChooseSupplier(item)}
                                      variant={'text'}
                                      className="w-full justify-start gap-1 px-2 py-1.5 min-h-0 hover:bg-accent hover:text-accent-foreground cursor-pointer select-none"
                                    >
                                      <LucideCheck
                                        className={`shrink-0 lucide lucide-check h-4 w-4 ${supplier?.id === item?.id ? 'opacity-100' : 'opacity-0'}`}
                                      />
                                      <span className="font-medium text-foreground">
                                        {item?.displayName || ''}
                                      </span>
                                      <span className="text-muted-foreground">
                                        ({item?.numberOfComponents || 0})
                                      </span>
                                    </Button>
                                  );
                                })}
                              </PopoverContent>
                            </Popover>
                          </div>
                          {mode === 'update' && (
                            <>
                              <div className="flex flex-col gap-2">
                                <Label htmlFor="eta-date">Created</Label>
                                <div className="h-10 w-[180px] flex items-center px-3 text-sm text-foreground border border-input rounded-md bg-muted/30">
                                  {createdDate ? moment(createdDate).format('MM/DD/YY') : '—'}
                                </div>
                              </div>
                              <div className="flex flex-col gap-2">
                                <Label htmlFor="eta-date">Sent</Label>
                                <div
                                  className={cn(
                                    'inline-flex items-center gap-1 group text-xs',
                                    sentDate ? '[&:hover>.builder-eta-date-x]:opacity-100' : ''
                                  )}
                                >
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button
                                        data-qa={`${mode}_po_eta_date_trigger`}
                                        type="button"
                                        className={cn(
                                          'inline-flex items-center gap-2 whitespace-nowrap rounded-md ring-offset-background transition-colors hover-visible:outline-none hover-visible:ring-2 hover-visible:ring-ring hover-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background px-4 py-2 justify-start text-left font-normal text-sm hover:text-primary hover:border-primary/50 h-10 w-[180px]',
                                          !sentDate ? 'text-muted-foreground' : ''
                                        )}
                                      >
                                        <LucideCalendar className="lucide lucide-calendar mr-2 h-4 w-4" />
                                        {sentDate
                                          ? moment(sentDate).format('MM/DD/YY')
                                          : 'MM/DD/YY'}
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent align="start" className="w-auto p-0">
                                      <Calendar
                                        data-qa={`${mode}_po_eta_date_calendar`}
                                        mode="single"
                                        selected={sentDate}
                                        className="rounded-md border-0 p-[12px]"
                                        onSelect={(v) => handleSelectDate(v, 'sentDate')}
                                      />
                                    </PopoverContent>
                                  </Popover>

                                  {sentDate && (
                                    <Button
                                      data-qa={`${mode}_po_eta_date_clear`}
                                      onClick={() => handleRemoveDate('sentDate')}
                                      className="builder-eta-date-x p-0 opacity-0 h-[unset]"
                                      variant={'x_btn'}
                                    >
                                      <LucideX className="lucide lucide-x h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </>
                          )}
                          <div className="flex flex-col gap-2">
                            <Label htmlFor="eta-date">ETA</Label>
                            <div
                              className={cn(
                                'inline-flex items-center gap-1 group text-xs',
                                etaDate ? '[&:hover>.builder-eta-date-x]:opacity-100' : ''
                              )}
                            >
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    data-qa={`${mode}_po_eta_date_trigger`}
                                    type="button"
                                    className={cn(
                                      'inline-flex items-center gap-2 whitespace-nowrap rounded-md ring-offset-background transition-colors hover-visible:outline-none hover-visible:ring-2 hover-visible:ring-ring hover-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background px-4 py-2 justify-start text-left font-normal text-sm hover:text-primary hover:border-primary/50 h-10 w-[180px]',
                                      !etaDate ? 'text-muted-foreground' : ''
                                    )}
                                  >
                                    <LucideCalendar className="lucide lucide-calendar mr-2 h-4 w-4" />
                                    {etaDate ? moment(etaDate).format('MM/DD/YY') : 'MM/DD/YY'}
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent align="start" className="w-auto p-0">
                                  <Calendar
                                    data-qa={`${mode}_po_eta_date_calendar`}
                                    mode="single"
                                    selected={etaDate}
                                    className="rounded-md border-0 p-[12px]"
                                    onSelect={(v) => handleSelectDate(v, 'etaDate')}
                                    disabled={{ before: new Date() }}
                                  />
                                </PopoverContent>
                              </Popover>

                              {etaDate && (
                                <Button
                                  data-qa={`${mode}_po_eta_date_clear`}
                                  onClick={() => handleRemoveDate('etaDate')}
                                  className="builder-eta-date-x p-0 opacity-0 h-[unset]"
                                  variant={'x_btn'}
                                >
                                  <LucideX className="lucide lucide-x h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {!loading ? (
                        <div className="px-4 py-6 md:px-8 md:py-8">
                          <div className="w-full space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-y-6 md:gap-x-6 lg:justify-between text-[12px] items-start">
                              <div className="self-stretch flex flex-col">
                                <div>
                                  <div className="mb-2 flex items-baseline gap-3">
                                    <div className="font-bold text-foreground text-[13px]">
                                      Supplier Information
                                    </div>
                                    {mode !== 'view' && (
                                      <Link
                                        to={`/suppliers${supplier?.id ? `/${supplier?.id}?openModal=true` : ''}`}
                                        target="_blank"
                                        className="text-xs text-primary hover:text-primary/80 font-medium underline underline-offset-2"
                                      >
                                        Edit
                                      </Link>
                                      // <Button
                                      //   onClick={() => handleShowSupplierModal(true)}
                                      //   variant={'link'}
                                      //   className="p-0 m-0 min-h-0 h-max text-xs font-medium underline underline-offset-2"
                                      // >
                                      //   Edit
                                      // </Button>
                                    )}
                                  </div>
                                  <div className="space-y-3">
                                    <div className="flex gap-2">
                                      <div className="font-bold text-foreground shrink-0 w-[70px]">
                                        Name:
                                      </div>
                                      <div className="text-muted-foreground flex-1 min-w-0 wrap-text">
                                        {supplier?.displayName || '—'}
                                      </div>
                                    </div>
                                    <div className="flex gap-2">
                                      <div className="font-bold text-foreground shrink-0 w-[70px]">
                                        Phone:
                                      </div>
                                      <div className="text-muted-foreground flex-1 min-w-0 wrap-text">
                                        {supplier?.mainPhone || '—'}
                                      </div>
                                    </div>
                                    <div className="flex gap-2">
                                      <div className="font-bold text-foreground shrink-0 w-[70px]">
                                        Email:
                                      </div>
                                      <div className="text-muted-foreground flex-1 min-w-0 wrap-text">
                                        {supplier?.shippingAddress?.email || '—'}
                                      </div>
                                    </div>
                                    <div className="flex gap-2">
                                      <div className="font-bold text-foreground shrink-0 w-[70px]">
                                        Address:
                                      </div>
                                      <div className="text-muted-foreground leading-snug flex-1 min-w-0">
                                        {!showAddressStreet() && !showAddress() ? (
                                          '—'
                                        ) : (
                                          <>
                                            <div className="wrap-text">{showAddressStreet()}</div>
                                            <div className="wrap-text">{showAddress()}</div>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex gap-2">
                                      <div className="font-bold text-foreground shrink-0 w-[70px]">
                                        Doc Ref:
                                      </div>
                                      <div className="text-muted-foreground flex-1 min-w-0">
                                        <Input
                                          id="docRef"
                                          data-qa="docRef"
                                          name="docRef"
                                          value={docRef}
                                          onChange={handleDocRef}
                                          placeholder="Enter"
                                          className="min-h-[32px] h-8 text-foreground"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                {duplicateFabricWarning && (
                                  <div className="mt-auto pt-4">
                                    <div className="flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2 text-[12px] text-destructive w-max">
                                      <LucideTriangleAlert className="lucide lucide-triangle-alert h-4 w-4 shrink-0 mt-0.5" />
                                      <div>
                                        <span className="font-bold">Important:</span> Combine same
                                        fabric into a single cut.
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="min-w-0">
                                <div>
                                  <div className="mb-2 flex items-baseline gap-3">
                                    <div className="font-bold text-foreground text-[13px]">
                                      Shipping Information
                                    </div>
                                  </div>
                                  <div className="space-y-3">
                                    <div className="flex gap-2">
                                      <div className="font-bold text-foreground shrink-0 w-[88px]">
                                        How to Ship:
                                      </div>
                                      <div className="text-muted-foreground flex-1 min-w-0">
                                        <Input
                                          id="shipping-info-how-to-ship"
                                          data-qa="shipping-info-how-to-ship"
                                          name="howToShip"
                                          value={shippingInformations.howToShip || ''}
                                          onChange={handleShippingInfo}
                                          placeholder="Enter"
                                          className="min-h-[32px] h-8 text-foreground"
                                        />
                                      </div>
                                    </div>
                                    <div className="flex gap-2">
                                      <div className="font-bold text-foreground shrink-0 w-[88px]">
                                        Ship to:
                                      </div>
                                      <div className="text-muted-foreground flex-1 min-w-0">
                                        <Textarea
                                          id="shipping-info-ship-to"
                                          data-qa="shipping-info-ship-to"
                                          name="shipTo"
                                          value={shippingInformations.shipTo || ''}
                                          onChange={handleShippingInfo}
                                          placeholder="Enter shipping address"
                                          className="flex rounded-md border border-input bg-background px-3 ring-offset-background placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 w-full resize-none min-h-0 py-1.5 text-[12px] leading-snug text-foreground"
                                          rows={5}
                                        />
                                      </div>
                                    </div>
                                    <div className="flex gap-2">
                                      <div className="font-bold text-foreground shrink-0 w-[88px]">
                                        Sidemark:
                                      </div>
                                      <div className="text-muted-foreground flex-1 min-w-0 wrap-text">
                                        <Input
                                          id="sidemark"
                                          name="sidemark"
                                          value={sidemark}
                                          onChange={handleChangeSidemark}
                                          className="min-h-[32px] h-8 text-foreground"
                                        />
                                      </div>
                                    </div>
                                    <div className="flex gap-2">
                                      <div className="font-bold text-foreground shrink-0 w-[88px]">
                                        Notes:
                                      </div>
                                      <div className="text-muted-foreground flex-1 min-w-0">
                                        <Textarea
                                          id="shipping-info-notes"
                                          data-qa="shipping-info-notes"
                                          name="notes"
                                          value={shippingInformations.notes || ''}
                                          onChange={handleShippingInfo}
                                          placeholder="Enter"
                                          className="flex rounded-md border border-input bg-background px-3 ring-offset-background placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 w-full resize-none min-h-0 h-[4.5rem] py-1.5 text-[12px] leading-snug text-foreground"
                                          rows={3}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div>
                                <div className="mb-2 flex items-baseline gap-3">
                                  <div className="font-bold text-foreground text-[13px]">
                                    Account Information
                                  </div>
                                  {mode !== 'view' && (
                                    <Link
                                      to={'/account_settings'}
                                      className="text-xs text-primary hover:text-primary/80 font-medium underline underline-offset-2"
                                    >
                                      Edit
                                    </Link>
                                  )}
                                </div>
                                <div className="space-y-3">
                                  <div className="flex gap-2">
                                    <div className="font-bold text-foreground shrink-0 w-[70px]">
                                      Business:
                                    </div>
                                    <div className="text-muted-foreground leading-snug flex-1 min-w-0">
                                      {!account?.clientFacingInfo?.companyName &&
                                      !showAccountInfoAddressStreet() &&
                                      !showAccountInfoAddress() &&
                                      !account?.clientFacingInfo?.phone &&
                                      !account?.clientFacingInfo?.email ? (
                                        '—'
                                      ) : (
                                        <>
                                          <div className="wrap-text font-bold text-foreground">
                                            {account?.clientFacingInfo?.companyName || ''}
                                          </div>
                                          <div className="wrap-text">
                                            {showAccountInfoAddressStreet()}
                                          </div>
                                          <div className="wrap-text">
                                            {showAccountInfoAddress()}
                                          </div>
                                          <div className="wrap-text">
                                            {account?.clientFacingInfo?.phone || ''}
                                          </div>
                                          <div className="wrap-text">
                                            {account?.clientFacingInfo?.email || ''}
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <div className="font-bold text-foreground shrink-0 w-[70px]">
                                      Contact:
                                    </div>
                                    <div className="text-muted-foreground flex-1 min-w-0">
                                      <Input
                                        id="account-info-contact"
                                        data-qa="account-info-contact"
                                        name="contact"
                                        value={accountInformations?.contact || ''}
                                        onChange={handleAccountInfo}
                                        placeholder="Enter name"
                                        className="min-h-[32px] h-8 text-foreground"
                                      />
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <div className="font-bold text-foreground shrink-0 w-[70px]">
                                      Account #:
                                    </div>
                                    <div className="text-muted-foreground flex-1 min-w-0 wrap-text">
                                      {supplier?.shippingAddress?.accountNumber || '—'}
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <div className="font-bold text-foreground shrink-0 w-[70px]">
                                      Terms:
                                    </div>
                                    <div className="text-muted-foreground flex-1 min-w-0 wrap-text">
                                      {supplier?.billingAddress?.paymentTerms || '—'}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="border-t border-input" />

                            <div className="flex gap-4 items-stretch">
                              <div className="w-[340px] shrink-0 border-r border-input self-stretch">
                                <div className="flex flex-col min-h-0 h-full w-full">
                                  <div className="relative overflow-hidden flex-1 min-h-0">
                                    <div className="h-full w-full rounded-[inherit]">
                                      <div>
                                        <div className="pr-4 pb-4 space-y-1">
                                          <h3 className="mt-0 mb-2 text-sm font-semibold text-foreground">
                                            Select Components
                                          </h3>

                                          <div className="mb-3 border-b border-input pb-3 flex items-center gap-2">
                                            <Checkbox
                                              checked={
                                                totalSelectedComponentsLength ===
                                                totalComponentsLength
                                              }
                                              onCheckedChange={(checked) =>
                                                handleSelectComponents(checked, 'all')
                                              }
                                              className="border-primary"
                                            />
                                            <span className="flex-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                                              Components{' '}
                                              {`(${totalSelectedComponentsLength}/${totalComponentsLength})`}
                                            </span>
                                          </div>

                                          {(visibleSupplierComponents || [])?.map((item: any) => {
                                            let room_components = item?.components || [];
                                            let room_key = getRoomGroupKey(item);

                                            let room_selected =
                                              selectedComponents?.find(
                                                (f: any) => getRoomGroupKey(f) === room_key
                                              )?.components?.length === room_components?.length;

                                            let room_total_components =
                                              room_components?.length || 0;
                                            let room_total_selected_components =
                                              selectedComponents?.find(
                                                (f: any) => getRoomGroupKey(f) === room_key
                                              )?.components?.length || 0;

                                            return (
                                              <div
                                                key={String(item?.roomId || room_key)}
                                                className="mb-1"
                                              >
                                                <div className="group flex items-center gap-2 rounded px-1 py-1.5 hover:bg-muted/50">
                                                  <Checkbox
                                                    checked={room_selected}
                                                    onCheckedChange={(checked) =>
                                                      handleSelectComponents(checked, 'room', item)
                                                    }
                                                    className="border-primary"
                                                  />

                                                  <button
                                                    onClick={() => handleOpenRooms(room_key)}
                                                    className="flex flex-1 items-center gap-1 text-left"
                                                  >
                                                    <LucideChevronDown
                                                      className={cn(
                                                        'lucide lucide-chevron-down h-3.5 w-3.5 text-muted-foreground',
                                                        !openedRooms?.includes(room_key)
                                                          ? '-rotate-90'
                                                          : ''
                                                      )}
                                                    />
                                                    <span className="text-sm font-medium text-foreground truncate">
                                                      {item?.roomName || ''}
                                                    </span>
                                                    <span className="ml-auto text-xs text-muted-foreground">
                                                      {`${room_total_selected_components}/${room_total_components}`}
                                                    </span>
                                                  </button>
                                                </div>

                                                {room_components?.length > 0 &&
                                                  openedRooms?.includes(room_key) && (
                                                    <div className="ml-6 space-y-0.5">
                                                      {room_components?.map((rc: any) => {
                                                        let rc_component = rc?.component || null;
                                                        let rc_order = rc?.order || null;
                                                        let component_archived =
                                                          isArchivedComponentRow(rc, item);

                                                        let rc_component_item =
                                                          rc_component?.patternObj ||
                                                          rc_component?.item ||
                                                          null;

                                                        let rc_component_item_desc =
                                                          rc_component_item?.orderingDescription ||
                                                          '';

                                                        let rc_order_summary =
                                                          rc_order?.draperyOrderSummary;

                                                        let rc_order_invoice_json =
                                                          rc_order_summary?.invoiceJson || null;

                                                        let rc_order_invoice_status =
                                                          normalizeInvoiceStatus(
                                                            rc_order_invoice_json?.paid || null
                                                          );

                                                        let rc_po_status = getComponentPOStatus({
                                                          ...rc,
                                                          roomId: rc?.roomId ?? item?.roomId,
                                                          roomName: rc?.roomName ?? item?.roomName,
                                                        });

                                                        let component_selected = selectedComponents
                                                          ?.find(
                                                            (i: any) =>
                                                              getRoomGroupKey(i) === room_key
                                                          )
                                                          ?.components?.some(
                                                            (s: any) =>
                                                              s?.component?.id === rc_component?.id
                                                          );

                                                        return (
                                                          <Label
                                                            key={rc_component?.id}
                                                            className={cn(
                                                              'flex items-start gap-2 rounded px-2 py-1.5 transition-colors cursor-pointer font-normal',
                                                              component_selected
                                                                ? 'bg-primary/5'
                                                                : 'hover:bg-muted/40'
                                                            )}
                                                          >
                                                            <Checkbox
                                                              checked={component_selected}
                                                              onCheckedChange={(checked) =>
                                                                handleSelectComponents(
                                                                  checked,
                                                                  'single',
                                                                  item,
                                                                  rc
                                                                )
                                                              }
                                                              className="border-primary"
                                                            />

                                                            <div className="flex flex-1 flex-col gap-1 min-w-0">
                                                              <span className="truncate text-sm text-foreground">
                                                                {`${
                                                                  rc_component_item_desc
                                                                    ? rc_component_item_desc
                                                                    : determineNonMyShopItemDescription(
                                                                        rc
                                                                      )
                                                                }${
                                                                  component_archived
                                                                    ? ' (archived)'
                                                                    : ''
                                                                }`}
                                                              </span>

                                                              <div className="flex flex-wrap items-center gap-1.5">
                                                                <div
                                                                  className={cn(
                                                                    'inline-flex items-center rounded-full border font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 shrink-0 border-transparent px-1.5 py-0 text-[10px] leading-tight hover:bg-current/0',
                                                                    determineStatusColor(
                                                                      rc_order_invoice_status
                                                                    )
                                                                  )}
                                                                >
                                                                  Invoice:{' '}
                                                                  {rc_order_invoice_status || ''}
                                                                </div>

                                                                <div
                                                                  className={cn(
                                                                    'inline-flex items-center rounded-full border font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 shrink-0 border-transparent hover:bg-current/0 px-1.5 py-0 text-[10px] leading-tight',
                                                                    getPoBadgeClass(
                                                                      rc_po_status.hasPO
                                                                    )
                                                                  )}
                                                                >
                                                                  PO: {rc_po_status.label}
                                                                </div>
                                                                {rc_po_status.extraCount > 0 && (
                                                                  <div className="inline-flex items-center rounded-full border font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 shrink-0 border-transparent hover:bg-current/0 px-1.5 py-0 text-[10px] leading-tight bg-blue-100 text-blue-700">
                                                                    +{rc_po_status.extraCount}
                                                                  </div>
                                                                )}
                                                              </div>
                                                            </div>
                                                          </Label>
                                                        );
                                                      })}
                                                    </div>
                                                  )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="min-w-0 space-y-6 flex-1">
                                <div className="rounded-lg overflow-x-auto">
                                  <div className="relative w-full overflow-auto">
                                    <Table className="caption-bottom text-sm min-w-[820px] w-full table-fixed">
                                      <colgroup>
                                        <col className="w-[12%]" />
                                        <col className="w-[9%]" />
                                        <col className="w-[44%]" />
                                        <col className="w-[11%]" />
                                        <col className="w-[12%]" />
                                        <col className="w-[12%]" />
                                      </colgroup>
                                      <TableHeader className="sticky top-0 bg-background z-10">
                                        <TableRow className="transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted bg-muted/50">
                                          <TableHead className="h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4">
                                            Item #
                                          </TableHead>
                                          <TableHead className="h-12 px-1.5 align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 text-center">
                                            Photo
                                          </TableHead>
                                          <TableHead className="h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4">
                                            Ordering Description
                                          </TableHead>
                                          <TableHead className="h-12 px-1.5 align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 text-right pr-4">
                                            Qty
                                          </TableHead>
                                          <TableHead className="h-12 px-1.5 align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 text-right">
                                            Unit Cost
                                          </TableHead>
                                          <TableHead className="h-12 px-1.5 align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 text-right">
                                            Amount
                                          </TableHead>
                                        </TableRow>
                                      </TableHeader>

                                      <TableBody>
                                        {totalSelectedComponentsLength ? (
                                          selectedComponents?.flatMap((sc_room: any) => {
                                            return (sc_room?.components || [])?.map((comp: any) => {
                                              let sc_room_component = comp?.component || null;
                                              let component_archived = isArchivedComponentRow(
                                                comp,
                                                sc_room
                                              );

                                              let sc_room_component_item =
                                                sc_room_component?.patternObj ||
                                                sc_room_component?.item ||
                                                null;

                                              let sc_room_component_item_ordering_description =
                                                sc_room_component_item?.orderingDescription;

                                              return (
                                                <TableRow key={sc_room_component?.id}>
                                                  <TableCell className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 text-sm">
                                                    <div>
                                                      <span className="wrap-text">
                                                        {sc_room_component_item?.itemNumber || ''}
                                                      </span>
                                                    </div>
                                                  </TableCell>
                                                  <TableCell className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 text-center">
                                                    <div className="flex items-center justify-center">
                                                      {sc_room_component_item?.avatarId ? (
                                                        <div className="w-8 h-8 rounded-md border border-solid border-border overflow-hidden">
                                                          <img
                                                            className="w-full h-full object-cover"
                                                            src={resolveImageSrc(sc_room_component_item?.avatarId)}
                                                          />
                                                        </div>
                                                      ) : (
                                                        <span>—</span>
                                                      )}
                                                    </div>
                                                  </TableCell>
                                                  <TableCell className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 text-sm">
                                                    <div className="text-foreground">
                                                      <span className="wrap-text">
                                                        {`${
                                                          sc_room_component_item_ordering_description
                                                            ? sc_room_component_item_ordering_description
                                                            : determineNonMyShopItemDescription(
                                                                comp
                                                              )
                                                        }${
                                                          component_archived ? ' (archived)' : ''
                                                        }`}
                                                      </span>
                                                    </div>
                                                    <div className="mt-1.5 flex items-start gap-2">
                                                      <span className="text-xs font-medium text-muted-foreground shrink-0 pt-1.5">
                                                        Note:
                                                      </span>
                                                      <Textarea
                                                        id={`${sc_room_component?.id}_po_notes_component`}
                                                        name="poNotes"
                                                        value={sc_room_component?.poNotes || ''}
                                                        onChange={(e) =>
                                                          handleChangeComponentPoNote(
                                                            e,
                                                            sc_room,
                                                            comp
                                                          )
                                                        }
                                                        onInput={(e) =>
                                                          resizeTextarea(e.currentTarget, 26, true)
                                                        }
                                                        style={{
                                                          overflow: 'hidden',
                                                          minHeight: '26px',
                                                          height: '26px',
                                                        }}
                                                        placeholder="Add a note"
                                                        className="flex w-full rounded-md border border-input bg-background px-3 ring-offset-background placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[26px] py-1 text-xs text-foreground resize-none overflow-hidden leading-snug"
                                                      />
                                                    </div>
                                                  </TableCell>
                                                  <TableCell className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 text-sm text-right pr-4 whitespace-nowrap">
                                                    {componentQuantity(comp)?.quantity}
                                                    {/* {sc_room_component?.qty ||
                                                      sc_room_component?.quantity ||
                                                      '—'} */}
                                                  </TableCell>
                                                  <TableCell className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 text-sm text-right">
                                                    {/* {valuteNumberFormatter(
                                                      !resolveItemBillingTypeClientBased(
                                                        client,
                                                        sc_room_component?.item
                                                      )?.method?.includes('markUp')
                                                        ? resolveItemBillingTypeClientBased(
                                                            client,
                                                            sc_room_component?.item
                                                          )?.value
                                                        : sc_room_component?.cost
                                                    )} */}
                                                    {valuteNumberFormatter(
                                                      determineItemCost(comp, client)
                                                    )}
                                                  </TableCell>
                                                  <TableCell className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 text-sm last:text-right">
                                                    {valuteNumberFormatter(
                                                      // (!resolveItemBillingTypeClientBased(
                                                      //   client,
                                                      //   sc_room_component?.item
                                                      // )?.method?.includes('markUp')
                                                      //   ? resolveItemBillingTypeClientBased(
                                                      //       client,
                                                      //       sc_room_component?.item
                                                      //     )?.value
                                                      //   : sc_room_component?.cost || 0) *
                                                      determineItemCost(comp, client) *
                                                        componentQuantity(comp)?.quantityNumber
                                                      // (sc_room_component?.qty ||
                                                      //   sc_room_component?.quantity ||
                                                      //   1)
                                                    )}
                                                  </TableCell>
                                                </TableRow>
                                              );
                                            });
                                          })
                                        ) : (
                                          <TableRow className="border-b border-input transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                                            <TableCell
                                              colSpan={6}
                                              className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-6 text-center text-muted-foreground text-sm"
                                            >
                                              No components selected. Choose components on the left
                                              to include them in this PO.
                                            </TableCell>
                                          </TableRow>
                                        )}
                                      </TableBody>
                                    </Table>
                                  </div>
                                </div>

                                <div className="flex justify-end pr-4">
                                  <div className="w-full md:w-[520px] text-[12px]">
                                    <div>
                                      <div className="flex items-center justify-between py-2 text-muted-foreground">
                                        <span>Subtotal</span>
                                        <span>{valuteNumberFormatter(pricing.subtotal)}</span>
                                      </div>

                                      <div className="border-t border-input" />
                                    </div>

                                    {priceAdjustments &&
                                      priceAdjustments?.length > 0 &&
                                      priceAdjustments?.map((item: InitialPriceAdjustmentType) => {
                                        return (
                                          <div
                                            key={item?.id}
                                            className="flex items-center gap-2 py-2 border-b border-input"
                                          >
                                            <Input
                                              id={`${item?.id}_label_price_adj`}
                                              name="name"
                                              placeholder="Label (e.g. Shipping, Tax, Discount)"
                                              value={item?.name || ''}
                                              onChange={(e) =>
                                                handlePriceAdjustments('edit', {
                                                  item: item,
                                                  name: e?.target?.name,
                                                  value: e?.target?.value,
                                                })
                                              }
                                              className="flex w-full rounded-md border border-input bg-background px-3 py-2 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-8 text-sm flex-1"
                                            />

                                            <div className="relative w-[160px]">
                                              <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                  <Button
                                                    data-qa={`${item?.id}_unit_price_adj_trigger`}
                                                    variant={'text'}
                                                    className={cn(
                                                      'bg-transparent flex items-center justify-between border-none top-1/2 -translate-y-1/2 py-2 ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 absolute left-0 min-h-8 h-8 w-[58px] rounded-none px-[8px] text-sm focus:ring-0 focus:ring-offset-0'
                                                    )}
                                                  >
                                                    <span className="pointer-events-none">
                                                      {item?.type === 'amount' ? '$' : '%'}
                                                    </span>
                                                    <LucideChevronDown className="lucide lucide-chevron-down h-4 w-4 opacity-50" />
                                                  </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent
                                                  className="z-[99999990]"
                                                  align="start"
                                                >
                                                  <DropdownMenuItem
                                                    onClick={() =>
                                                      handlePriceAdjustments('edit', {
                                                        item: item,
                                                        name: 'type',
                                                        value: 'amount',
                                                      })
                                                    }
                                                  >
                                                    $
                                                  </DropdownMenuItem>
                                                  <DropdownMenuItem
                                                    onClick={() =>
                                                      handlePriceAdjustments('edit', {
                                                        item: item,
                                                        name: 'type',
                                                        value: 'percent',
                                                      })
                                                    }
                                                  >
                                                    %
                                                  </DropdownMenuItem>
                                                </DropdownMenuContent>
                                              </DropdownMenu>

                                              <Input
                                                id={`${item?.id}_value_price_adj`}
                                                name="value"
                                                placeholder="±0.00"
                                                value={item?.value || ''}
                                                onBlur={(e) =>
                                                  handleBlurPriceAdjustments({
                                                    item: item,
                                                    name: e.target.name,
                                                    value: e.target.value,
                                                  })
                                                }
                                                onChange={(e) =>
                                                  handlePriceAdjustments('edit', {
                                                    item: item,
                                                    name: e?.target?.name,
                                                    value: e?.target?.value,
                                                  })
                                                }
                                                className="flex w-full rounded-md border border-input bg-background px-3 py-2 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-8 text-sm text-right pl-[64px]"
                                              />
                                            </div>

                                            <Button
                                              variant={'trash-button'}
                                              onClick={() =>
                                                handlePriceAdjustments('remove', {
                                                  item: item,
                                                })
                                              }
                                            >
                                              <Trash2 className="lucide lucide-trash2 h-4 w-4" />
                                            </Button>
                                          </div>
                                        );
                                      })}

                                    <div className="py-1.5">
                                      <Button
                                        onClick={() => handlePriceAdjustments('add')}
                                        variant={'link'}
                                        className="font-medium hover:no-underline min-h-[32px] py-0 hover:bg-accent px-2"
                                      >
                                        <LucidePlus className="lucide lucide-plus h-4 w-4" />
                                        <span>Add charge / discount</span>
                                      </Button>
                                    </div>

                                    <div className="border-t border-input" />

                                    <div>
                                      <div className="flex items-center justify-between py-2 text-muted-foreground">
                                        <span>Order Total</span>
                                        <span>
                                          {valuteNumberFormatter(
                                            pricing?.orderTotal,
                                            'en-US',
                                            'USD',
                                            true
                                          )}
                                        </span>
                                      </div>

                                      <div className="border-t border-input" />
                                    </div>

                                    <div className="flex items-center justify-between py-2 border-b border-input text-muted-foreground">
                                      <span>Paid Amount</span>

                                      <div className="relative w-[140px]">
                                        <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                                          $
                                        </span>

                                        <Input
                                          id="paidAmount"
                                          name="paidAmount"
                                          value={paidAmount}
                                          onChange={handlePaidAmount}
                                          onBlur={handlePaidAmountBlur}
                                          className="min-h-[32px] text-right h-8 py-2 px-3 pl-5"
                                          placeholder="0.00"
                                        />
                                      </div>
                                    </div>

                                    <div>
                                      <div className="flex items-center justify-between py-2 text-foreground font-bold text-[14px]">
                                        <span>Amount Due</span>
                                        <span>
                                          {valuteNumberFormatter(
                                            pricing?.amountDue,
                                            'en-US',
                                            'USD',
                                            true
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <Loader className="h-[50%]" />
                      )}
                    </>
                  )
                ) : mode === 'view' ? (
                  // VIEW MODE
                  <>
                    <div className="border-b border-input bg-background px-4 py-4 md:px-6 md:py-5">
                      <div className="flex flex-wrap items-end gap-4">
                        <div className="space-y-2 w-full max-w-md">
                          <Label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            Supplier
                          </Label>
                          <div className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1">
                            <span className="pointer-events: none;">
                              {supplier?.displayName || '—'}
                            </span>
                            <LucideChevronDown className="lucide lucide-chevron-down h-4 w-4 opacity-50" />
                          </div>
                        </div>

                        <div className="flex flex-col gap-2">
                          <Label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            Created
                          </Label>
                          <div className="h-10 w-[180px] flex items-center px-3 text-sm text-foreground border border-input rounded-md bg-muted/30">
                            {createdDate ? moment(createdDate).format('MM/DD/YY') : '—'}
                          </div>
                        </div>

                        <div className="flex flex-col gap-2">
                          <Label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            Sent
                          </Label>
                          <div className="h-10 w-[180px] flex items-center px-3 text-sm text-foreground border border-input rounded-md bg-muted/30">
                            {sentDate ? moment(sentDate).format('MM/DD/YY') : '—'}
                          </div>
                        </div>

                        <div className="flex flex-col gap-2">
                          <Label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            ETA
                          </Label>
                          <div className="h-10 w-[180px] flex items-center px-3 text-sm text-foreground border border-input rounded-md bg-muted/30">
                            {etaDate ? moment(etaDate).format('MM/DD/YY') : '—'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {!loading ? (
                      <div className="px-4 py-6 md:px-8 md:py-8">
                        <div className="w-full space-y-6">
                          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-y-6 md:gap-x-6 lg:justify-between text-[12px] items-start">
                            <div className="self-stretch flex flex-col">
                              <div>
                                <div className="mb-2 flex items-baseline gap-3">
                                  <div className="font-bold text-foreground text-[13px]">
                                    Supplier Information
                                  </div>
                                  {mode !== 'view' && (
                                    <Link
                                      to={`/suppliers${supplier?.id ? `/${supplier?.id}?openModal=true` : ''}`}
                                      target="_blank"
                                      className="text-xs text-primary hover:text-primary/80 font-medium underline underline-offset-2"
                                    >
                                      Edit
                                    </Link>
                                    // <Button
                                    //   onClick={() => handleShowSupplierModal(true)}
                                    //   variant={'link'}
                                    //   className="p-0 m-0 min-h-0 h-max text-xs font-medium underline underline-offset-2"
                                    // >
                                    //   Edit
                                    // </Button>
                                  )}
                                </div>
                                <div className="space-y-3">
                                  <div className="flex gap-2">
                                    <div className="font-bold text-foreground shrink-0 w-[70px]">
                                      Name:
                                    </div>
                                    <div className="text-muted-foreground flex-1 min-w-0 wrap-text">
                                      {supplier?.displayName || '—'}
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <div className="font-bold text-foreground shrink-0 w-[70px]">
                                      Phone:
                                    </div>
                                    <div className="text-muted-foreground flex-1 min-w-0 wrap-text">
                                      {supplier?.mainPhone || '—'}
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <div className="font-bold text-foreground shrink-0 w-[70px]">
                                      Email:
                                    </div>
                                    <div className="text-muted-foreground flex-1 min-w-0 wrap-text">
                                      {supplier?.shippingAddress?.email || '—'}
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <div className="font-bold text-foreground shrink-0 w-[70px]">
                                      Address:
                                    </div>
                                    <div className="text-muted-foreground leading-snug flex-1 min-w-0">
                                      {!showAddressStreet() && !showAddress() ? (
                                        '—'
                                      ) : (
                                        <>
                                          <div className="wrap-text">{showAddressStreet()}</div>
                                          <div className="wrap-text">{showAddress()}</div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <div className="font-bold text-foreground shrink-0 w-[70px]">
                                      Doc Ref:
                                    </div>
                                    <div className="text-muted-foreground flex-1 min-w-0 wrap-text">
                                      {docRef || '—'}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {duplicateFabricWarning && (
                                <div className="mt-auto pt-4">
                                  <div className="flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2 text-[12px] text-destructive w-max">
                                    <LucideTriangleAlert className="lucide lucide-triangle-alert h-4 w-4 shrink-0 mt-0.5" />
                                    <div>
                                      <span className="font-bold">Important:</span> Combine same
                                      fabric into a single cut.
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="min-w-0">
                              <div>
                                <div className="mb-2 flex items-baseline gap-3">
                                  <div className="font-bold text-foreground text-[13px]">
                                    Shipping Information
                                  </div>
                                </div>
                                <div className="space-y-3">
                                  <div className="flex gap-2">
                                    <div className="font-bold text-foreground shrink-0 w-[88px]">
                                      How to Ship:
                                    </div>
                                    <div className="text-muted-foreground flex-1 min-w-0 wrap-text">
                                      {shippingInformations.howToShip || '—'}
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <div className="font-bold text-foreground shrink-0 w-[88px]">
                                      Ship to:
                                    </div>
                                    <div className="leading-slug text-muted-foreground flex-1 min-w-0 wrap-text whitespace-pre-line">
                                      {shippingInformations.shipTo || '—'}
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <div className="font-bold text-foreground shrink-0 w-[88px]">
                                      Sidemark:
                                    </div>
                                    <div className="text-muted-foreground flex-1 min-w-0 wrap-text">
                                      {sidemark}
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <div className="font-bold text-foreground shrink-0 w-[88px]">
                                      Notes:
                                    </div>
                                    <div className="text-muted-foreground flex-1 min-w-0 wrap-text whitespace-pre-line">
                                      {shippingInformations.notes || '—'}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div>
                              <div className="mb-2 flex items-baseline gap-3">
                                <div className="font-bold text-foreground text-[13px]">
                                  Account Information
                                </div>
                                {mode !== 'view' && (
                                  <Link
                                    to={'/account_settings'}
                                    className="text-xs text-primary hover:text-primary/80 font-medium underline underline-offset-2"
                                  >
                                    Edit
                                  </Link>
                                )}
                              </div>
                              <div className="space-y-3">
                                <div className="flex gap-2">
                                  <div className="font-bold text-foreground shrink-0 w-[70px]">
                                    Business:
                                  </div>
                                  <div className="text-muted-foreground leading-snug flex-1 min-w-0">
                                    {!account?.clientFacingInfo?.companyName &&
                                    !showAccountInfoAddressStreet() &&
                                    !showAccountInfoAddress() &&
                                    !account?.clientFacingInfo?.phone &&
                                    !account?.clientFacingInfo?.email ? (
                                      '—'
                                    ) : (
                                      <>
                                        <div className="wrap-text font-bold text-foreground">
                                          {account?.clientFacingInfo?.companyName}
                                        </div>
                                        <div className="wrap-text">
                                          {showAccountInfoAddressStreet()}
                                        </div>
                                        <div className="wrap-text">{showAccountInfoAddress()}</div>
                                        <div className="wrap-text">
                                          {account?.clientFacingInfo?.phone}
                                        </div>
                                        <div className="wrap-text">
                                          {account?.clientFacingInfo?.email}
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <div className="font-bold text-foreground shrink-0 w-[70px]">
                                    Contact:
                                  </div>
                                  <div className="text-muted-foreground flex-1 min-w-0 wrap-text">
                                    {accountInformations?.contact || '—'}
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <div className="font-bold text-foreground shrink-0 w-[70px]">
                                    Account #:
                                  </div>
                                  <div className="text-muted-foreground flex-1 min-w-0 wrap-text">
                                    {supplier?.shippingAddress?.accountNumber || '—'}
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <div className="font-bold text-foreground shrink-0 w-[70px]">
                                    Terms:
                                  </div>
                                  <div className="text-muted-foreground flex-1 min-w-0 wrap-text">
                                    {supplier?.billingAddress?.paymentTerms || '—'}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="border-t border-input" />

                          <div>
                            <div className="min-w-0 space-y-6 w-full">
                              <div className="border border-input rounded-lg overflow-x-auto">
                                <div className="relative w-full overflow-auto">
                                  <Table
                                    wrapperClassName={cn('max-w-full md:max-w-full')}
                                    className={cn(
                                      'caption-bottom text-sm min-w-[820px] w-full table-fixed'
                                    )}
                                  >
                                    <colgroup>
                                      <col className="w-[12%]" />
                                      <col className="w-[9%]" />
                                      <col className="w-[44%]" />
                                      <col className="w-[11%]" />
                                      <col className="w-[12%]" />
                                      <col className="w-[12%]" />
                                    </colgroup>
                                    <TableHeader className="sticky top-0 bg-background z-10">
                                      <TableRow className="transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted bg-muted/50">
                                        <TableHead className="h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4">
                                          Item #
                                        </TableHead>
                                        <TableHead className="h-12 px-1.5 align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 text-center">
                                          Photo
                                        </TableHead>
                                        <TableHead className="h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4">
                                          Ordering Description
                                        </TableHead>
                                        <TableHead className="h-12 px-1.5 align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 text-right pr-4">
                                          Qty
                                        </TableHead>
                                        <TableHead className="h-12 px-1.5 align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 text-right">
                                          Unit Cost
                                        </TableHead>
                                        <TableHead className="h-12 px-1.5 align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 text-right">
                                          Amount
                                        </TableHead>
                                      </TableRow>
                                    </TableHeader>

                                    <TableBody>
                                      {totalSelectedComponentsLength ? (
                                        selectedComponents?.flatMap((sc_room: any) => {
                                          return (sc_room?.components || [])?.map((comp: any) => {
                                            let sc_room_component = comp?.component || null;
                                            let component_archived = isArchivedComponentRow(
                                              comp,
                                              sc_room
                                            );

                                            let sc_room_component_item =
                                              sc_room_component?.patternObj ||
                                              sc_room_component?.item ||
                                              null;

                                            let sc_room_component_item_ordering_description =
                                              sc_room_component_item?.orderingDescription;

                                            return (
                                              <TableRow key={sc_room_component?.id}>
                                                <TableCell className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 text-sm">
                                                  <div>
                                                    <span className="wrap-text">
                                                      {sc_room_component_item?.itemNumber || ''}
                                                    </span>
                                                  </div>
                                                </TableCell>
                                                <TableCell className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 text-center">
                                                  <div className="flex items-center justify-center">
                                                    {sc_room_component_item?.avatarId ? (
                                                      <div className="w-8 h-8 rounded-md border border-solid border-border overflow-hidden">
                                                        <img
                                                          className="w-full h-full object-cover"
                                                          src={resolveImageSrc(sc_room_component_item?.avatarId)}
                                                        />
                                                      </div>
                                                    ) : (
                                                      <span>—</span>
                                                    )}
                                                  </div>
                                                </TableCell>
                                                <TableCell className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 text-sm">
                                                  <div className="text-foreground">
                                                    <span className="wrap-text">
                                                      {`${
                                                        sc_room_component_item_ordering_description
                                                          ? sc_room_component_item_ordering_description
                                                          : determineNonMyShopItemDescription(comp)
                                                      }${component_archived ? ' (archived)' : ''}`}
                                                    </span>
                                                  </div>
                                                  <div className="mt-1.5 flex items-start gap-2">
                                                    <span className="leading-snug text-xs font-medium text-muted-foreground shrink-0">
                                                      Note:
                                                    </span>
                                                    <span className="text-xs text-foreground wrap-text whitespace-pre-line leading-snug">
                                                      {sc_room_component?.poNotes || ''}
                                                    </span>
                                                  </div>
                                                </TableCell>
                                                <TableCell className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 text-sm text-right pr-4 whitespace-nowrap">
                                                  {componentQuantity(comp)?.quantity}
                                                  {/* {sc_room_component?.qty ||
                                                    sc_room_component?.quantity ||
                                                    '—'} */}
                                                </TableCell>
                                                <TableCell className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 text-sm text-right">
                                                  {/* {valuteNumberFormatter(
                                                    !resolveItemBillingTypeClientBased(
                                                      client,
                                                      sc_room_component?.item
                                                    )?.method?.includes('markUp')
                                                      ? resolveItemBillingTypeClientBased(
                                                          client,
                                                          sc_room_component?.item
                                                        )?.value
                                                      : sc_room_component?.cost
                                                  )} */}
                                                  {valuteNumberFormatter(
                                                    determineItemCost(comp, client)
                                                  )}
                                                </TableCell>
                                                <TableCell className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 text-sm last:text-right">
                                                  {valuteNumberFormatter(
                                                    // (!resolveItemBillingTypeClientBased(
                                                    //   client,
                                                    //   sc_room_component?.item
                                                    // )?.method?.includes('markUp')
                                                    //   ? resolveItemBillingTypeClientBased(
                                                    //       client,
                                                    //       sc_room_component?.item
                                                    //     )?.value
                                                    //   : sc_room_component?.cost || 0) *
                                                    determineItemCost(comp, client) *
                                                      componentQuantity(comp)?.quantityNumber
                                                    // (sc_room_component?.qty ||
                                                    //   sc_room_component?.quantity ||
                                                    //   1)
                                                  )}
                                                </TableCell>
                                              </TableRow>
                                            );
                                          });
                                        })
                                      ) : (
                                        <TableRow className="border-b border-input transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                                          <TableCell
                                            colSpan={6}
                                            className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-6 text-center text-muted-foreground text-sm"
                                          >
                                            No components selected. Choose components on the left to
                                            include them in this PO.
                                          </TableCell>
                                        </TableRow>
                                      )}
                                    </TableBody>
                                  </Table>
                                </div>
                              </div>

                              <div className="flex justify-end pr-4">
                                <div className="w-full md:w-[520px] text-[12px]">
                                  <div>
                                    <div className="flex items-center justify-between py-2 text-muted-foreground">
                                      <div>Subtotal</div>
                                      <div>{valuteNumberFormatter(pricing.subtotal)}</div>
                                    </div>

                                    <div className="border-t border-input" />
                                  </div>

                                  {priceAdjustments &&
                                    priceAdjustments?.length > 0 &&
                                    priceAdjustments?.map((item: InitialPriceAdjustmentType) => {
                                      return (
                                        <div key={item?.id}>
                                          <div className="flex items-center justify-between py-2 text-muted-foreground">
                                            <span>
                                              {item?.name || ''}{' '}
                                              {item?.type === 'percent' && `(${item?.value}%)`}
                                            </span>

                                            {item?.type === 'percent' ? (
                                              <span>
                                                {valuteNumberFormatter(
                                                  pricing?.subtotal *
                                                    ((Number(item?.value) || 0) / 100),
                                                  'en-US',
                                                  'USD',
                                                  true
                                                )}
                                              </span>
                                            ) : (
                                              <span>
                                                {valuteNumberFormatter(
                                                  item?.value,
                                                  'en-US',
                                                  'USD',
                                                  true
                                                )}
                                              </span>
                                            )}
                                          </div>

                                          <div className="border-t border-input" />
                                        </div>
                                      );
                                    })}

                                  <div className="border-t border-input" />

                                  <div>
                                    <div className="flex items-center justify-between py-2 text-muted-foreground">
                                      <span>Order Total</span>
                                      <span>
                                        {valuteNumberFormatter(
                                          pricing?.orderTotal,
                                          'en-US',
                                          'USD',
                                          true
                                        )}
                                      </span>
                                    </div>

                                    <div className="border-t border-input" />
                                  </div>

                                  <div>
                                    <div className="flex items-center justify-between py-2 text-muted-foreground">
                                      <span>Paid Amount</span>
                                      <span>{valuteNumberFormatter(paidAmount || 0)}</span>
                                    </div>

                                    <div className="border-t border-input" />
                                  </div>

                                  <div>
                                    <div className="flex items-center justify-between py-2 text-foreground font-bold text-[14px]">
                                      <span>Amount Due</span>
                                      <span>
                                        {valuteNumberFormatter(
                                          pricing?.amountDue,
                                          'en-US',
                                          'USD',
                                          true
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <Loader className="h-[50%]" />
                    )}
                  </>
                ) : null}
              </div>
            </div>
          </div>

          <SheetFooter className="shrink-0 flex items-center justify-end gap-2 border-t border-input px-4 py-3 md:px-6 md:py-4 bg-background">
            {mode === 'create' ? (
              <>
                <Button
                  onClick={() => handleSavePO()}
                  disabled={loading || saving || sending || !supplier}
                  className="m-0"
                  variant={'create-outline'}
                >
                  <span>{saving ? 'Saving...' : 'Save'}</span>
                </Button>
                <Button
                  onClick={() => handleOpenSendDialog()}
                  disabled={loading || saving || sending || !supplier || isCancelledPO}
                  className="m-0"
                  title={sendDisabledReason || undefined}
                >
                  <LucideSend className="lucide lucide-send h-4 w-4" />
                  <span>Save & Send</span>
                </Button>
              </>
            ) : mode === 'view' ? (
              <>
                <Button
                  onClick={() => handleEditRequest()}
                  className="m-0"
                  variant={'create-outline'}
                >
                  <LucidePencil className="lucide lucide-pencil h-4 w-4" />
                  <span>Edit</span>
                </Button>
                <Button
                  onClick={() => handleOpenSendDialog()}
                  className="m-0"
                  disabled={sending || isCancelledPO}
                  title={sendDisabledReason || undefined}
                >
                  <LucideSend className="lucide lucide-send h-4 w-4" />
                  <span>Send PO</span>
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => handleCancelEditMode()}
                  className="m-0"
                  variant={'text'}
                  disabled={sending}
                >
                  <span>Cancel</span>
                </Button>
                <Button
                  onClick={() => handleSavePO()}
                  className="m-0"
                  variant={'create-outline'}
                  disabled={saving || sending}
                >
                  <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                </Button>
                <Button
                  onClick={() => handleOpenSendDialog()}
                  className="m-0"
                  disabled={saving || sending || isCancelledPO}
                  title={sendDisabledReason || undefined}
                >
                  <LucideSend className="lucide lucide-send h-4 w-4" />
                  <span>Save & Send</span>
                </Button>
              </>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <SendPODialog
        open={sendDialogOpen}
        poNumber={poNumber || row?.poNumber || generatePoNumber()}
        orderDate={createdDate || row?.createdAt || new Date()}
        supplier={supplier}
        account={account}
        user={user}
        sidemark={sidemark}
        isSending={sending}
        requiresSaveBeforeSend={mode !== 'view'}
        disabledReason={sendDisabledReason}
        onOpenChange={handleSendDialogOpenChange}
        onSend={handleSendPO}
      />

      <AlertDialog open={alertModal.active}>
        <AlertDialogContent>
          <AlertDialogHeader className="flex flex-col space-y-2 text-center sm:text-left gap-0">
            <h2 className="m-0 text-lg font-semibold">{alertModal?.title || ''}</h2>
            <p className="text-sm text-muted-foreground">{alertModal?.description || ''}</p>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 gap-0">
            <Button
              onClick={alertModal?.cancel?.action}
              className="font-medium"
              variant={'outline'}
            >
              {alertModal?.cancel?.title || ''}
            </Button>
            <Button
              onClick={alertModal?.confirm?.action}
              className="font-medium"
              variant={'default'}
            >
              {alertModal?.confirm?.title || ''}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* <SupplierModal
        accountId={account?.id}
        isModalOpen={supplierModal.active}
        onModalClose={handleCloseSupplierModal}
        onSave={handleSupplierChanged}
        resetOnClose
        initialValues={supplier}
        style={{ zIndex: 99999999 }}
      /> */}
    </>
  );
};

export default POsBuilder;
